import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Shield, Building2, Users, FileText, Search, Eye, EyeOff, Clock,
  ChevronDown, AlertTriangle, Check, X, Loader2, LogOut,
  Activity, Database, Settings, RefreshCw, Crown, Edit2,
  HelpCircle, Plus, Trash2, Save, UserPlus, TestTube,
  TrendingUp, HardDrive, Star, MessageSquare, Send, Key,
  Table, Mail
} from 'lucide-react';
import { TIERS, ROLES, FEATURES, PERMISSIONS_MATRIX, getAccess } from '@/permissions/permissionsMatrix';

const SUPERADMIN_EMAIL = 'timothy.herd@gmail.com';

const AdminDashboardPage = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [adminStatus, setAdminStatus] = useState(null);
  const [organisations, setOrganisations] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('organisations');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [editingOrgId, setEditingOrgId] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Quick Stats state
  const [quickStats, setQuickStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Help Guides state
  const [helpGuides, setHelpGuides] = useState([]);
  const [loadingGuides, setLoadingGuides] = useState(false);
  const [savingGuides, setSavingGuides] = useState(false);
  const [editingGuide, setEditingGuide] = useState(null);
  const [newItemText, setNewItemText] = useState({ question: '', answer: '' });

  // Test Accounts state
  const [testAccounts, setTestAccounts] = useState([]);
  const [allTestAccounts, setAllTestAccounts] = useState([]);
  const [creatingTestAccounts, setCreatingTestAccounts] = useState(false);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [newAccountData, setNewAccountData] = useState({ name: '', email: '', password: '', subscription_tier: 'pro' });
  const [deletingAccountId, setDeletingAccountId] = useState(null);
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [resettingPasswordId, setResettingPasswordId] = useState(null);

  // Admin Accounts state
  const [adminAccounts, setAdminAccounts] = useState([]);
  const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
  const [newAdminData, setNewAdminData] = useState({ name: '', email: '', password: '', role: 'admin' });
  const [creatingAdmin, setCreatingAdmin] = useState(false);

  // Transfer Ownership Modal state
  const [transferModalOrg, setTransferModalOrg] = useState(null);
  const [transferOwnerEmail, setTransferOwnerEmail] = useState('');
  const [transferringOwnership, setTransferringOwnership] = useState(false);
  const [deletingAdminId, setDeletingAdminId] = useState(null);
  // Delete Org state
  const [deleteModalOrg, setDeleteModalOrg] = useState(null);
  const [deletingOrg, setDeletingOrg] = useState(false);
  const [deleteOrgConfirmText, setDeleteOrgConfirmText] = useState('');

  // View Members Modal state
  const [membersModalOrg, setMembersModalOrg] = useState(null);
  const [orgMembers, setOrgMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Subscription Override state
  const [overrideModalOrg, setOverrideModalOrg] = useState(null);
  const [overrideType, setOverrideType] = useState('system_default');
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideExpiry, setOverrideExpiry] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);

  // Emission Factors state
  const [emissionFactors, setEmissionFactors] = useState([]);
  const [factorYears, setFactorYears] = useState([]);
  const [selectedFactorYear, setSelectedFactorYear] = useState(2026);
  const [loadingFactors, setLoadingFactors] = useState(false);
  const [editingFactor, setEditingFactor] = useState(null);
  const [newFactor, setNewFactor] = useState({
    factor_year: 2027,
    category: '',
    state: '',
    sub_type: '',
    factor_value: '',
    factor_unit: 'kWh',
    scope: 'Scope 2',
    source: 'NGA Table 1',
    nga_source_edition: 'NGA Factors 2026',
    notes: ''
  });
  const [showAddFactor, setShowAddFactor] = useState(false);
  const [savingFactor, setSavingFactor] = useState(false);

  // Category Visibility Mapping state
  const [categoryMappings, setCategoryMappings] = useState([]);
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [updatingMapping, setUpdatingMapping] = useState(null);

  // Pending Purges state
  const [pendingPurges, setPendingPurges] = useState([]);
  const [loadingPurges, setLoadingPurges] = useState(false);
  const [executingPurge, setExecutingPurge] = useState(null);

  // Frozen Orgs state
  const [frozenOrgs, setFrozenOrgs] = useState([]);
  const [loadingFrozen, setLoadingFrozen] = useState(false);

  // Support Module state
  const [supportThreads, setSupportThreads] = useState([]);
  const [loadingSupport, setLoadingSupport] = useState(false);
  const [selectedSupportThread, setSelectedSupportThread] = useState(null);
  const [supportMessages, setSupportMessages] = useState([]);
  const [loadingSupportMessages, setLoadingSupportMessages] = useState(false);
  const [supportReply, setSupportReply] = useState('');
  const [sendingSupportReply, setSendingSupportReply] = useState(false);
  const [supportStatusFilter, setSupportStatusFilter] = useState('all');
  const [replyTemplates, setReplyTemplates] = useState([]);

  // Platform Settings state
  const [platformLogo, setPlatformLogo] = useState(null);
  const [platformLogoUrl, setPlatformLogoUrl] = useState('/placeholder-logo.svg');
  const [uploadingPlatformLogo, setUploadingPlatformLogo] = useState(false);
  const [platformSettings, setPlatformSettings] = useState({
    business_name: 'Lucent Sky ESG',
    business_email: 'hello@lucentsky.com.au',
    business_phone: '',
    business_address: '64 Meadow Court, Doonan, QLD, 4562',
    business_abn: '37 351 283 491'
  });
  const [savingPlatformSettings, setSavingPlatformSettings] = useState(false);

  // Users tab state
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [sendingResetId, setSendingResetId] = useState(null);

  const [deleteModalUser, setDeleteModalUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [deleteUserConfirmText, setDeleteUserConfirmText] = useState('');

  const [confirmRemoveMember, setConfirmRemoveMember] = useState(null); // { user_id, org_id, name, email }

  const [editUserModal, setEditUserModal] = useState(null);
  const [editUserName, setEditUserName] = useState('');
  const [savingUserName, setSavingUserName] = useState(false);

  const [orgManageModalUser, setOrgManageModalUser] = useState(null);
  const [managingOrgUser, setManagingOrgUser] = useState(false);
  const [selectedOrgToAdd, setSelectedOrgToAdd] = useState('');
  const [selectedRoleToAdd, setSelectedRoleToAdd] = useState('viewer');

  useEffect(() => {
    checkAdminStatus();
  }, []);

  // Helper to call admin Edge Function
  const callAdminFunction = async (action, data = {}) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        console.error("No active session found in callAdminFunction");
        throw new Error("No active session. Please log in again.");
      }
      const { data: result, error } = await supabase.functions.invoke('admin-operations', {
        body: { action, data }
      });

      if (error) {
        // Try to get more info from the error
        console.error("Edge Function error object:", error);
        if (error.context) {
          try {
            const errorBody = await error.context.json();
            console.error("Edge Function error body:", errorBody);
            throw new Error(errorBody.details || errorBody.error || error.message);
          } catch (e) {
            console.error("Could not parse error context as JSON", e);
          }
        }
        throw error;
      }

      return result;
    } catch (err) {
      console.error(`Detailed error in callAdminFunction for ${action}:`, err);
      throw err;
    }
  };

  const checkAdminStatus = async () => {
    try {
      // Get current auth user
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        navigate('/login');
        return;
      }

      // Check if user is superadmin
      const { data: userData, error } = await supabase
        .from('users')
        .select('id, email, name, is_superadmin')
        .eq('supabase_user_id', authUser.id)
        .single();

      if (error || !userData?.is_superadmin) {
        toast.error('Access denied. SuperAdmin privileges required.');
        navigate('/dashboard');
        return;
      }

      setAdminStatus(userData);
      fetchOrganisations();
      fetchAuditLogs();
      fetchQuickStats();
      fetchAllUsers();
      fetchPlatformLogo();
      fetchPlatformSettings();
    } catch (error) {
      console.error('Error checking admin status:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuickStats = async () => {
    setLoadingStats(true);
    try {
      // Get user counts
      const { count: totalUsers } = await supabase.from('users').select('*', { count: 'exact', head: true });

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);

      const { count: newToday } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', today.toISOString());

      const { count: newThisWeek } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', weekAgo.toISOString());

      // Get org counts
      const { count: totalOrgs } = await supabase.from('organisations').select('*', { count: 'exact', head: true });

      // Get subscription breakdown
      const { data: orgsData } = await supabase.from('organisations').select('subscription_status');
      const subCounts = { trial: 0, pro: 0, standard: 0, cancelled: 0 };
      orgsData?.forEach(org => {
        const status = org.subscription_status || 'trial';
        if (subCounts[status] !== undefined) subCounts[status]++;
      });

      // Get emissions counts
      const { count: totalEntries } = await supabase.from('emissions_data').select('*', { count: 'exact', head: true });
      const { count: entriesThisWeek } = await supabase
        .from('emissions_data')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', weekAgo.toISOString());

      setQuickStats({
        users: { total: totalUsers || 0, new_today: newToday || 0, new_this_week: newThisWeek || 0, active_this_week: newThisWeek || 0 },
        organisations: { total: totalOrgs || 0, ...subCounts },
        emissions: { total_entries: totalEntries || 0, entries_this_week: entriesThisWeek || 0 },
        storage: { total_mb: 0 }
      });
    } catch (error) {
      console.error('Error fetching quick stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchOrganisations = async () => {
    try {
      const { data, error } = await supabase
        .from('organisations')
        .select(`
          *,
          owner:users!organisations_owner_id_fkey(id, email, name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOrganisations(data || []);
    } catch (error) {
      console.error('Error fetching organisations:', error);
      toast.error('Failed to load organisations');
    }
  };

  const fetchAuditLogs = async (orgId = null) => {
    try {
      let query = supabase
        .from('audit_logs')
        .select(`
          *,
          user:users(name, email),
          organisation:organisations(name)
        `)
        .eq('is_admin_action', true)
        .order('created_at', { ascending: false })
        .limit(100);

      if (orgId) query = query.eq('organisation_id', orgId);

      const { data, error } = await query;
      if (error) throw error;
      setAuditLogs(data || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      // If we still get a relationship error, it might be due to schema cache.
      // The join on organisations should now work after the FK was added.
    }
  };

  const fetchPendingPurges = async () => {
    setLoadingPurges(true);
    try {
      const { data, error } = await supabase
        .from('organisations')
        .select('*, owner:users!organisations_owner_id_fkey(email, name)')
        .not('purge_scheduled_at', 'is', null)
        .is('purge_executed_at', null);

      if (error) throw error;
      setPendingPurges(data || []);
    } catch (error) {
      console.error('Error fetching pending purges:', error);
      toast.error('Failed to load pending purges');
    } finally {
      setLoadingPurges(false);
    }
  };

  const cancelPurge = async (orgId, orgName) => {
    if (!window.confirm(`Are you sure you want to cancel the scheduled purge for "${orgName}"?`)) return;

    try {
      await callAdminFunction('cancel_purge', { organisation_id: orgId });
      toast.success(`Purge cancelled for ${orgName}`);
      fetchPendingPurges();
    } catch (error) {
      console.error('Error cancelling purge:', error);
      toast.error('Failed to cancel purge');
    }
  };

  const executePurge = async (orgId, orgName) => {
    if (!window.confirm(`⚠️ WARNING: This will PERMANENTLY DELETE all data for "${orgName}". This cannot be undone. Continue?`)) return;
    if (!window.confirm(`Please confirm again: DELETE ALL DATA for "${orgName}"?`)) return;

    setExecutingPurge(orgId);
    try {
      await callAdminFunction('execute_purge', { organisation_id: orgId });
      toast.success(`Data purged for ${orgName}`);
      fetchPendingPurges();
    } catch (error) {
      console.error('Error executing purge:', error);
      toast.error('Failed to execute purge');
    } finally {
      setExecutingPurge(null);
    }
  };

  const fetchFrozenOrgs = async () => {
    setLoadingFrozen(true);
    try {
      const { data, error } = await supabase
        .from('organisations')
        .select('*, owner:users!organisations_owner_id_fkey(email, name)')
        .eq('is_frozen', true);

      if (error) throw error;
      setFrozenOrgs(data || []);
    } catch (error) {
      console.error('Error fetching frozen orgs:', error);
      toast.error('Failed to load frozen organisations');
    } finally {
      setLoadingFrozen(false);
    }
  };

  // Support Module functions
  const fetchSupportThreads = async () => {
    setLoadingSupport(true);
    try {
      let query = supabase
        .from('support_threads')
        .select(`
          *,
          organisations(name),
          support_messages(id, is_read, sender_type, body, created_at)
        `)
        .order('last_message_at', { ascending: false });

      if (supportStatusFilter !== 'all') {
        query = query.eq('status', supportStatusFilter);
      }

      const { data, error } = await query;
      if (error) throw error;

      const threadsWithMeta = (data || []).map(thread => ({
        ...thread,
        unread_count: thread.support_messages?.filter(m => m.sender_type === 'user' && !m.is_read).length || 0,
        last_message: thread.support_messages?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
      }));

      setSupportThreads(threadsWithMeta);
    } catch (error) {
      console.error('Error fetching support threads:', error);
      toast.error('Failed to load support threads');
    } finally {
      setLoadingSupport(false);
    }
  };

  const fetchSupportMessages = async (threadId) => {
    setLoadingSupportMessages(true);
    try {
      const { data, error } = await supabase
        .from('support_messages')
        .select(`
          *,
          sender:users(id, name, email)
        `)
        .eq('thread_id', threadId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setSupportMessages(data || []);

      // Mark user messages as read
      const unreadUserMsgs = (data || []).filter(m => m.sender_type === 'user' && !m.is_read);
      if (unreadUserMsgs.length > 0) {
        await supabase
          .from('support_messages')
          .update({ is_read: true })
          .in('id', unreadUserMsgs.map(m => m.id));
      }
    } catch (error) {
      console.error('Error fetching support messages:', error);
    } finally {
      setLoadingSupportMessages(false);
    }
  };

  const fetchReplyTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('support_reply_templates')
        .select('*')
        .eq('is_active', true)
        .order('title');

      if (error) throw error;
      setReplyTemplates(data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleSendSupportReply = async () => {
    if (!supportReply.trim() || !selectedSupportThread) {
      toast.error('Please enter a reply');
      return;
    }

    setSendingSupportReply(true);
    try {
      // Get admin user
      const { data: adminUser } = await supabase
        .from('users')
        .select('id')
        .eq('email', user?.email)
        .single();

      // Create message
      const { error } = await supabase
        .from('support_messages')
        .insert({
          thread_id: selectedSupportThread.id,
          sender_id: adminUser?.id,
          sender_type: 'admin',
          body: supportReply
        });

      if (error) throw error;

      // Send notification email to customer
      const orgOwnerEmail = selectedSupportThread.organisations?.owner_email;
      if (orgOwnerEmail) {
        try {
          await supabase.functions.invoke('send-email', {
            body: {
              to: orgOwnerEmail,
              subject: 'New Message from Lucent Sky Support',
              html: `
                <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                  <h1 style="color: #003B5C;">New Secure Message</h1>
                  <p>Hello,</p>
                  <p>A new message is waiting for you in the Lucent Sky Message Centre regarding your enquiry:</p>
                  <p style="background: #f5f5f5; padding: 15px; border-left: 4px solid #003B5C; margin: 20px 0;">
                    <strong>${selectedSupportThread.subject}</strong>
                  </p>
                  <p style="margin: 30px 0;">
                    <a href="https://app.lucentsky.com.au/messages" style="background-color: #003B5C; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px;">
                      View Message
                    </a>
                  </p>
                  <p style="color: #888; font-size: 14px; border-top: 1px solid #eee; padding-top: 20px; margin-top: 30px;">
                    <strong>For your security, please do not reply to this email.</strong><br>
                    Log in to Lucent Sky to view and respond to this message.
                  </p>
                  <p>Best regards,<br>The Lucent Sky Team</p>
                </div>
              `
            }
          });
        } catch (emailError) {
          console.error('Failed to send notification email:', emailError);
        }
      }

      toast.success('Reply sent & customer notified');
      setSupportReply('');
      await fetchSupportMessages(selectedSupportThread.id);
      await fetchSupportThreads();
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    } finally {
      setSendingSupportReply(false);
    }
  };

  const handleCloseThread = async (threadId) => {
    try {
      await callAdminFunction('close_support_thread', { thread_id: threadId });
      toast.success('Thread closed');
      fetchSupportThreads();
    } catch (error) {
      console.error('Error closing thread:', error);
      toast.error('Failed to close thread');
    }
  };

  const handleReopenThread = async (threadId) => {
    try {
      await callAdminFunction('reopen_support_thread', { thread_id: threadId });
      toast.success('Thread reopened');
      fetchSupportThreads();
    } catch (error) {
      console.error('Error reopening thread:', error);
      toast.error('Failed to reopen thread');
    }
  };

  // Platform Settings functions
  const fetchPlatformLogo = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'platform_logo')
        .maybeSingle();

      if (error) {
        console.error('Error fetching platform logo:', error);
        return;
      }

      if (data?.value) {
        const parsed = typeof data.value === 'string' ? JSON.parse(data.value) : data.value;
        if (parsed?.logo_url) {
          setPlatformLogoUrl(parsed.logo_url);
        }
      }
    } catch (error) {
      console.error('Error fetching platform logo:', error);
    }
  };

  const handlePlatformLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PNG, JPEG, SVG, or WebP file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }

    setUploadingPlatformLogo(true);
    try {
      // Upload to Supabase Storage
      const fileName = `platform-logo-${Date.now()}.${file.name.split('.').pop()}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('platform-assets')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('platform-assets')
        .getPublicUrl(fileName);

      // Save to system_settings
      const { error: settingsError } = await supabase.from('system_settings').upsert({
        id: 'platform_logo',
        key: 'platform_logo',
        value: JSON.stringify({ logo_url: publicUrl }),
        updated_at: new Date().toISOString()
      }, { onConflict: 'key' });

      if (settingsError) {
        console.error('Error saving logo to system_settings:', settingsError);
        toast.error('Logo uploaded but failed to save setting: ' + settingsError.message);
        return;
      }

      setPlatformLogoUrl(publicUrl);
      toast.success('Platform logo updated successfully!');
    } catch (error) {
      console.error('Error uploading platform logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingPlatformLogo(false);
      e.target.value = ''; // Reset file input
    }
  };

  const fetchPlatformSettings = async () => {
    try {
      const { data } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'platform_contact')
        .single();

      if (data?.value) {
        setPlatformSettings({
          business_name: data.value.business_name || 'Lucent Sky ESG',
          business_email: data.value.business_email || 'hello@lucentsky.com.au',
          business_phone: data.value.business_phone || '',
          business_address: data.value.business_address || '64 Meadow Court, Doonan, QLD, 4562',
          business_abn: data.value.business_abn || '37 351 283 491'
        });
      }
    } catch (error) {
      console.error('Error fetching platform settings:', error);
    }
  };

  const handleSavePlatformSettings = async () => {
    setSavingPlatformSettings(true);
    try {
      await callAdminFunction('update_system_settings', {
        key: 'platform_contact',
        value: platformSettings
      });
      toast.success('Platform contact details saved successfully!');
    } catch (error) {
      console.error('Error saving platform settings:', error);
      toast.error('Failed to save platform settings');
    } finally {
      setSavingPlatformSettings(false);
    }
  };

  const fetchHelpGuides = async () => {
    setLoadingGuides(true);
    try {
      const { data, error } = await supabase
        .from('help_guides')
        .select('*, help_guide_items(*)');

      if (error) throw error;

      // Convert to array format for UI
      const guidesArray = (data || []).map(guide => ({
        key: guide.key || guide.id,
        title: guide.title,
        icon: guide.icon || 'Book',
        items: (guide.help_guide_items || []).map(item => ({
          question: item.question,
          answer: item.answer
        }))
      }));
      setHelpGuides(guidesArray);
    } catch (error) {
      console.error('Error fetching help guides:', error);
      toast.error('Failed to load help guides');
    } finally {
      setLoadingGuides(false);
    }
  };

  const saveHelpGuides = async () => {
    setSavingGuides(true);
    try {
      // For each guide, update or insert
      for (const guide of helpGuides) {
        // Upsert the guide
        const { data: savedGuide, error: guideError } = await supabase
          .from('help_guides')
          .upsert({
            key: guide.key,
            title: guide.title,
            icon: guide.icon || 'Book',
          }, { onConflict: 'key' })
          .select()
          .single();

        if (guideError) throw guideError;

        // Delete existing items and re-insert
        await supabase
          .from('help_guide_items')
          .delete()
          .eq('guide_id', savedGuide.id);

        // Insert new items
        if (guide.items?.length > 0) {
          await supabase.from('help_guide_items').insert(
            guide.items.map(item => ({
              guide_id: savedGuide.id,
              question: item.question,
              answer: item.answer
            }))
          );
        }
      }
      toast.success('Help guides saved successfully');
    } catch (error) {
      console.error('Error saving help guides:', error);
      toast.error('Failed to save help guides');
    } finally {
      setSavingGuides(false);
    }
  };

  // ============ Emission Factor Functions ============
  const fetchFactorYears = async () => {
    try {
      const { data, error } = await supabase
        .from('emission_factors')
        .select('factor_year')
        .order('factor_year', { ascending: false });

      if (error) throw error;

      const years = [...new Set(data?.map(f => f.factor_year) || [])];
      setFactorYears(years.length > 0 ? years : [2026]);
      if (years.length > 0 && !years.includes(selectedFactorYear)) {
        setSelectedFactorYear(years[0]);
      }
    } catch (error) {
      console.error('Error fetching factor years:', error);
    }
  };

  const fetchEmissionFactors = async (year = selectedFactorYear) => {
    setLoadingFactors(true);
    try {
      const { data, error } = await supabase
        .from('emission_factors')
        .select('*')
        .eq('factor_year', year)
        .order('category');

      if (error) throw error;
      setEmissionFactors(data || []);
    } catch (error) {
      console.error('Error fetching emission factors:', error);
      toast.error('Failed to load emission factors');
    } finally {
      setLoadingFactors(false);
    }
  };

  const updateEmissionFactor = async (factorId, newValue, notes = null) => {
    setSavingFactor(true);
    try {
      const updates = { factor_value: newValue };
      if (notes) updates.notes = notes;

      await callAdminFunction('update_emission_factor', {
        id: factorId,
        updates
      });

      toast.success('Emission factor updated');
      setEditingFactor(null);
      fetchEmissionFactors(selectedFactorYear);
    } catch (error) {
      console.error('Error updating factor:', error);
      toast.error('Failed to update factor');
    } finally {
      setSavingFactor(false);
    }
  };

  const createEmissionFactor = async () => {
    setCreatingFactor(true);
    try {
      const factor = {
        ...newFactor, // Assuming newFactor is the state variable holding new factor data
        is_active: true,
        // Set effective dates based on factor_year (FY starts July 1)
        effective_from: `${newFactor.factor_year - 1}-07-01`,
        effective_to: `${newFactor.factor_year}-06-30`
      };

      await callAdminFunction('create_emission_factor', { factor });

      toast.success('Emission factor created');
      setNewFactor({
        factor_year: 2027,
        category: '',
        state: '',
        sub_type: '',
        factor_value: '',
        factor_unit: 'kWh',
        scope: 'Scope 2',
        source: 'NGA Table 1',
        nga_source_edition: 'NGA Factors 2026',
        notes: ''
      });
      fetchFactorYears();
      fetchEmissionFactors(newFactor.factor_year);
    } catch (error) {
      console.error('Error creating factor:', error);
      toast.error('Failed to create factor');
    } finally {
      setCreatingFactor(false);
    }
  };

  const deleteEmissionFactor = async (factorId) => {
    try {
      await callAdminFunction('delete_emission_factor', { id: factorId });
      toast.success('Emission factor deleted');
      fetchEmissionFactors(selectedFactorYear);
    } catch (error) {
      console.error('Error deleting factor:', error);
      toast.error('Failed to delete factor');
    }
  };

  const fetchCategoryMappings = async () => {
    setLoadingMappings(true);
    try {
      const { data, error } = await supabase
        .from('emission_category_mappings')
        .select('*')
        .order('scope', { ascending: true })
        .order('label', { ascending: true });

      if (error) throw error;
      setCategoryMappings(data || []);
    } catch (error) {
      console.error('Error fetching category mappings:', error);
      toast.error('Failed to load category visibility settings');
    } finally {
      setLoadingMappings(false);
    }
  };

  const toggleCategoryVisibility = async (key, currentVisibility) => {
    setUpdatingMapping(key);
    try {
      const { error } = await supabase
        .from('emission_category_mappings')
        .update({ is_visible: !currentVisibility })
        .eq('category_key', key);

      if (error) throw error;

      setCategoryMappings(prev => prev.map(m =>
        m.category_key === key ? { ...m, is_visible: !currentVisibility } : m
      ));

      toast.success(`${!currentVisibility ? 'Enabled' : 'Disabled'} ${key} in front-end GUI`);
    } catch (error) {
      console.error('Error updating visibility:', error);
      toast.error('Failed to update visibility');
    } finally {
      setUpdatingMapping(null);
    }
  };

  const addGuideItem = (guideKey) => {
    if (!newItemText.question || !newItemText.answer) {
      toast.error('Please fill in both question and answer');
      return;
    }

    setHelpGuides(prev => prev.map(guide => {
      if (guide.key === guideKey) {
        return {
          ...guide,
          items: [...guide.items, {
            id: `new-${Date.now()}`,
            question: newItemText.question,
            answer: newItemText.answer
          }]
        };
      }
      return guide;
    }));
    setNewItemText({ question: '', answer: '' });
  };

  const removeGuideItem = (guideKey, itemIndex) => {
    setHelpGuides(prev => prev.map(guide => {
      if (guide.key === guideKey) {
        return {
          ...guide,
          items: guide.items.filter((_, idx) => idx !== itemIndex)
        };
      }
      return guide;
    }));
  };

  const updateGuideItem = (guideKey, itemIndex, field, value) => {
    setHelpGuides(prev => prev.map(guide => {
      if (guide.key === guideKey) {
        const newItems = [...guide.items];
        newItems[itemIndex] = { ...newItems[itemIndex], [field]: value };
        return { ...guide, items: newItems };
      }
      return guide;
    }));
  };



  // Transfer Organisation Ownership (Admin Action)
  const handleAdminTransferOwnership = async () => {
    const email = transferOwnerEmail?.trim();
    if (!email || !transferModalOrg) return;

    setTransferringOwnership(true);
    try {
      const { data: users, error: userError } = await supabase
        .from('users')
        .select('id, name')
        .eq('email', email)
        .single();

      if (userError || !users) {
        toast.error('User not found with that email.');
        return;
      }

      const newOwnerId = users.id;
      const orgId = transferModalOrg.id;

      await callAdminFunction('transfer_ownership', {
        organisation_id: orgId,
        new_owner_id: newOwnerId
      });

      toast.success(`Ownership transferred to ${users.name || email}`);
      setTransferModalOrg(null);
      fetchOrganisations();

    } catch (err) {
      console.error('Error transferring ownership:', err);
      toast.error('Failed to transfer ownership.');
    } finally {
      setTransferringOwnership(false);
    }
  };

  // Open view members modal
  const handleViewMembers = async (org) => {
    setMembersModalOrg(org);
    setLoadingMembers(true);
    let memberships = null;
    let userIds = [];

    // Step 1: Try direct fetch
    try {
      const { data, error: memError } = await supabase
        .from('organisation_members')
        .select('user_id, role')
        .eq('organisation_id', org.id);

      if (memError) throw memError;
      memberships = data;
      userIds = [org.owner_id, ...(memberships || []).map(m => m.user_id)].filter(Boolean);
    } catch (err) {
      console.warn("Direct member fetch failed (Step 1), attempting local fallback:", err);

      // Local Fallback: Use allUsers state which is already enriched
      const localMembers = allUsers.filter(u =>
        u.orgRoles && u.orgRoles.some(r => r.org_id === org.id)
      ).map(u => ({
        role: u.orgRoles.find(r => r.org_id === org.id).role,
        user: { id: u.id, email: u.email, name: u.name }
      }));

      if (localMembers.length > 0) {
        setOrgMembers(localMembers);
        setLoadingMembers(false);
        return;
      }

      toast.error("Failed to load memberships. " + (err.message || ''));
      setLoadingMembers(false);
      return;
    }

    // Step 2: Fetch user details for those memberships
    let fixedMembers = [];
    try {
      if (userIds.length > 0) {
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, email, name')
          .in('id', userIds);

        if (usersError) throw usersError;

        const userMap = (usersData || []).reduce((acc, u) => ({ ...acc, [u.id]: u }), {});

        // Ensure owner is included and at the top
        if (org.owner_id && userMap[org.owner_id]) {
          fixedMembers.push({ role: 'owner', user: userMap[org.owner_id] });
        } else if (org.owner) {
          // Fallback if owner is in the org object but not returned in user query
          fixedMembers.push({ role: 'owner', user: org.owner });
        }

        (memberships || []).forEach(m => {
          if (m.user_id !== org.owner_id && userMap[m.user_id]) {
            fixedMembers.push({ role: m.role, user: userMap[m.user_id] });
          }
        });
      }
      setOrgMembers(fixedMembers);
    } catch (err) {
      console.error("Error fetching users (Step 2):", err);
      toast.error("Failed to load user details. " + (err.message || ''));
    } finally {
      setLoadingMembers(false);
    }
  };

  // Open override modal for an org
  const openOverrideModal = (org) => {
    setOverrideModalOrg(org);
    setOverrideType(org.subscription_override || 'system_default');
    setOverrideReason(org.subscription_override_reason || '');
    setOverrideExpiry(org.subscription_override_expiry ? org.subscription_override_expiry.split('T')[0] : '');
  };

  // Save subscription override
  const saveSubscriptionOverride = async () => {
    if (!overrideModalOrg) return;

    setSavingOverride(true);
    try {
      await callAdminFunction('subscription_override', {
        organisation_id: overrideModalOrg.id,
        override_type: overrideType,
        override_reason: overrideReason || null,
        override_expiry: overrideExpiry ? new Date(overrideExpiry).toISOString() : null
      });

      toast.success(
        overrideType === 'system_default'
          ? 'Subscription override removed'
          : `Organisation set to ${overrideType === 'manual_pro' ? 'Manual Pro' : 'Manual Trial'}`
      );

      setOverrideModalOrg(null);
      fetchOrganisations();
    } catch (error) {
      console.error('Error saving override:', error);
      toast.error('Failed to update subscription override');
    } finally {
      setSavingOverride(false);
    }
  };

  const checkTestAccounts = () => {
    // Check for test accounts in the organisations list
    const testEmails = ['testuser1@lucentsky.com.au', 'testuser2@lucentsky.com.au'];
    const found = organisations.filter(org =>
      testEmails.includes(org.owner_email?.toLowerCase())
    );
    setTestAccounts(found);
  };

  const fetchAllTestAccounts = async () => {
    try {
      // Fetch all users except superadmin
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('id, email, name, role, created_at, test_password, is_test_account')
        .neq('email', SUPERADMIN_EMAIL)
        .order('created_at', { ascending: false });

      if (usersError) throw usersError;

      // For each user, try to find their organisation (either as owner or member)
      const usersWithOrgs = await Promise.all((users || []).map(async (user) => {
        // Check if user owns an organisation
        const { data: ownedOrg } = await supabase
          .from('organisations')
          .select('id, name, stripe_subscription_status, effective_tier, override_tier')
          .eq('owner_id', user.id)
          .single();

        if (ownedOrg) {
          return { ...user, organisation: ownedOrg, org_role: 'owner' };
        }

        // Check if user is a member of an organisation
        const { data: membership } = await supabase
          .from('organisation_members')
          .select('role, organisations(id, name, stripe_subscription_status, effective_tier, override_tier)')
          .eq('user_id', user.id)
          .eq('invitation_status', 'accepted')
          .single();

        if (membership?.organisations) {
          return { ...user, organisation: membership.organisations, org_role: membership.role };
        }

        return { ...user, organisation: null, org_role: null };
      }));

      setAllTestAccounts(usersWithOrgs);
    } catch (error) {
      console.error('Error fetching test accounts:', error);
      toast.error('Failed to fetch user accounts');
    }
  };

  const createTestAccount = async () => {
    if (!newAccountData.name || !newAccountData.email || !newAccountData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setCreatingTestAccounts(true);
    try {
      const result = await callAdminFunction('create_test_account', {
        name: newAccountData.name,
        email: newAccountData.email,
        password: newAccountData.password,
        subscription_tier: newAccountData.subscription_tier
      });

      toast.success('Test account created');
      // Reset form and close modal
      setNewAccountData({ name: '', email: '', password: '', subscription_tier: 'pro' });
      setShowCreateAccountModal(false);

      // Refresh data
      fetchOrganisations();
      fetchAllTestAccounts();
    } catch (error) {
      console.error('Error creating test account:', error);
      toast.error('Failed to create test account');
    } finally {
      setCreatingTestAccounts(false);
    }
  };

  const deleteTestAccount = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete ${userEmail}? This action cannot be undone.`)) {
      return;
    }

    setDeletingAccountId(userId);
    try {
      await callAdminFunction('delete_test_account', { user_id: userId });

      toast.success(`Account ${userEmail} deleted`);

      // Refresh data
      fetchOrganisations();
      fetchAllTestAccounts();
    } catch (error) {
      console.error('Error deleting test account:', error);
      toast.error('Failed to delete test account');
    } finally {
      setDeletingAccountId(null);
    }
  };

  // Toggle password visibility
  const togglePasswordReveal = (userId) => {
    setRevealedPasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Reset/update test account password in database
  const resetTestAccountPassword = async (userId, userEmail) => {
    const newPassword = prompt(`Enter new password for ${userEmail}:`, 'Test123!');
    if (!newPassword) return;

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setResettingPasswordId(userId);
    try {
      // Update the test_password field in users table
      const { error } = await supabase
        .from('users')
        .update({ test_password: newPassword, is_test_account: true })
        .eq('id', userId);

      if (error) throw error;

      toast.success(`Password updated for ${userEmail}. Note: To change actual login password, user must use "Forgot Password" flow.`);
      fetchAllTestAccounts();
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error('Failed to update password');
    } finally {
      setResettingPasswordId(null);
    }
  };

  // Set password for existing account (first time)
  const setTestAccountPassword = async (userId, userEmail) => {
    const password = prompt(`Set password for ${userEmail} (this saves the password for admin reference):`, 'Test123!');
    if (!password) return;

    try {
      const { error } = await supabase
        .from('users')
        .update({ test_password: password, is_test_account: true })
        .eq('id', userId);

      if (error) throw error;

      toast.success(`Password saved for ${userEmail}`);
      fetchAllTestAccounts();
    } catch (error) {
      console.error('Error setting password:', error);
      toast.error('Failed to save password');
    }
  };

  const createTestAccounts = async () => {
    setCreatingTestAccounts(true);
    try {
      const result = await callAdminFunction('setup_test_accounts', {});

      toast.success('Test accounts setup initiated');
      // Refresh organisations list
      fetchOrganisations();
      fetchAllTestAccounts();
    } catch (error) {
      console.error('Error creating test accounts:', error);
      toast.error('Failed to create test accounts');
    } finally {
      setCreatingTestAccounts(false);
    }
  };

  const handleUpdateUserName = async () => {
    if (!editUserName.trim() || !editUserModal) {
      toast.error('Name cannot be empty');
      return;
    }

    setSavingUserName(true);
    try {
      await callAdminFunction('update_user', { user_id: editUserModal.id, name: editUserName });
      toast.success("User name updated successfully");
      setEditUserModal(null);
      // Refresh lists
      fetchAllUsers();
      fetchAllTestAccounts();
      fetchAdminAccounts();
    } catch (err) {
      console.error("Error updating user name:", err);
      toast.error("Failed to update user name");
    } finally {
      setSavingUserName(false);
    }
  };

  // ============ Admin Account Management ============
  const fetchAdminAccounts = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, email, name, is_superadmin, is_admin, created_at, role, supabase_user_id')
        .or('is_superadmin.eq.true,is_admin.eq.true');

      if (error) throw error;

      // Map the data to include computed role field
      const mappedAccounts = (data || []).map(admin => ({
        ...admin,
        role: admin.is_superadmin ? 'superadmin' : 'admin',
        is_primary_superadmin: admin.email === SUPERADMIN_EMAIL,
        has_supabase_auth: !!admin.supabase_user_id
      }));

      setAdminAccounts(mappedAccounts);
    } catch (error) {
      console.error('Error fetching admin accounts:', error);
    }
  };

  const createAdminAccount = async () => {
    if (!newAdminData.name || !newAdminData.email || !newAdminData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setCreatingAdmin(true);
    try {
      const result = await callAdminFunction('create_admin_account', {
        name: newAdminData.name,
        email: newAdminData.email,
        password: newAdminData.password,
        role: newAdminData.role
      });

      toast.success('Admin account created');
      // Reset form and close modal
      setNewAdminData({ name: '', email: '', password: '', role: 'admin' });
      setShowCreateAdminModal(false);

      // Refresh data
      fetchAdminAccounts();
    } catch (error) {
      console.error('Error creating admin account:', error);
      toast.error('Failed to create admin account');
    } finally {
      setCreatingAdmin(false);
    }
  };

  const deleteAdminAccount = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to remove admin privileges from ${userEmail}?`)) {
      return;
    }

    setDeletingAdminId(userId);
    try {
      await callAdminFunction('delete_admin_account', { user_id: userId });

      toast.success(`Admin privileges removed from ${userEmail}`);
      fetchAdminAccounts();
    } catch (error) {
      console.error('Error removing admin:', error);
      toast.error('Failed to remove admin privileges');
    } finally {
      setDeletingAdminId(null);
    }
  };

  // Check test accounts when organisations load
  useEffect(() => {
    if (organisations.length > 0) {
      checkTestAccounts();
    }
  }, [organisations]);

  // Fetch all users for the Users tab
  const fetchAllUsers = async () => {
    setLoadingUsers(true);
    try {
      // Use the unified admin function to bypass RLS issues
      const result = await callAdminFunction('get_all_users_enriched');

      const { users, memberships, organisations: orgs, tickets: supportThreads } = result;

      const orgsMap = (orgs || []).reduce((acc, o) => ({ ...acc, [o.id]: o }), {});

      // Enrich users with org info
      const enrichedUsers = (users || []).map(u => {
        const userMemberships = (memberships || []).filter(m => m.user_id === u.id);
        const ownedOrgs = (orgs || []).filter(o => o.owner_id === u.id);

        // Combine: owned orgs + memberships
        const orgRoles = [];
        ownedOrgs.forEach(o => {
          if (!orgRoles.find(r => r.org_id === o.id)) {
            orgRoles.push({ org_id: o.id, org_name: o.name, role: 'owner' });
          }
        });
        userMemberships.forEach(m => {
          if (!orgRoles.find(r => r.org_id === m.organisation_id)) {
            const org = orgsMap[m.organisation_id];
            orgRoles.push({ org_id: m.organisation_id, org_name: org?.name || 'Unknown', role: m.role });
          }
        });

        const userThreads = (supportThreads || []).filter(t => t.created_by === u.id);
        const support_tickets_raised = userThreads.length;
        const support_tickets_open = userThreads.filter(t => t.status !== 'resolved').length;

        return { ...u, orgRoles, support_tickets_raised, support_tickets_open };
      });

      setAllUsers(enrichedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSendPasswordReset = async (user) => {
    setSendingResetId(user.id);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast.success(`Password reset link sent to ${user.email}`);
    } catch (err) {
      console.error('Error sending reset email:', err);
      toast.error('Failed to send reset link.');
    } finally {
      setSendingResetId(null);
    }
  };

  const handleRemoveUserFromOrg = async (userId, orgId) => {
    try {
      await callAdminFunction('remove_user_from_org', {
        user_id: userId,
        organisation_id: orgId
      });

      toast.success('User removed from organisation.');

      // Update local state
      fetchAllUsers();
      if (orgManageModalUser?.id === userId) {
        setOrgManageModalUser(prev => ({
          ...prev,
          orgRoles: prev.orgRoles.filter(r => r.org_id !== orgId)
        }));
      }

      if (membersModalOrg?.id === orgId) {
        setOrgMembers(prev => prev.filter(m => m.user?.id !== userId));
      }
    } catch (err) {
      console.error('Error removing user from org:', err);
      toast.error('Failed to remove user from organisation.');
    } finally {
      if (confirmRemoveMember) {
        setConfirmRemoveMember(null);
      }
    }
  };

  const handleAddUserToOrg = async (userId, orgId, role) => {
    if (!orgId) return;
    setManagingOrgUser(true);
    try {
      await callAdminFunction('add_user_to_org', {
        user_id: userId,
        organisation_id: orgId,
        role: role
      });

      toast.success('User added to organisation.');

      fetchAllUsers();
      setSelectedOrgToAdd('');

      // Update modal state so it shows up instantly
      const org = organisations.find(o => o.id === orgId);
      if (orgManageModalUser?.id === userId && org) {
        setOrgManageModalUser(prev => ({
          ...prev,
          orgRoles: [...prev.orgRoles, { org_id: orgId, org_name: org.name, role }]
        }));
      }
    } catch (err) {
      console.error('Error adding user to org:', err);
      toast.error('Failed to add user to organisation.');
    } finally {
      setManagingOrgUser(false);
    }
  };

  const handleDeleteUser = async () => {
    if (deleteUserConfirmText !== deleteModalUser.email) return;
    setDeletingUser(true);
    try {
      await callAdminFunction('delete_user', { user_id: deleteModalUser.id });
      toast.success("User successfully deleted.");
      setDeleteModalUser(null);
      setDeleteUserConfirmText('');
      fetchAllUsers();
    } catch (err) {
      console.error("Delete user error:", err);
      toast.error("Failed to delete user. " + (err.message || ''));
    } finally {
      setDeletingUser(false);
    }
  };

  const updateOrgStatus = async (orgId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await callAdminFunction('update_org_status', {
        organisation_id: orgId,
        is_frozen: newStatus === 'frozen',
        freeze_reason: newStatus === 'frozen' ? 'Frozen by admin' : null
      });

      toast.success(`Status updated to ${newStatus.toUpperCase()}`);
      setEditingOrgId(null);
      fetchOrganisations();
      fetchAuditLogs();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const filteredOrgs = organisations.filter(org =>
    org.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.owner_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.abn?.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#74A57F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                SuperAdmin Console
              </h1>
              <p className="text-xs text-slate-400">{adminStatus?.email}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Exit Admin
            </Button>
            <Button
              onClick={signOut}
              variant="ghost"
              className="text-slate-400 hover:text-white"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Quick Stats Dashboard */}
      <div className="bg-slate-800/50 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          {/* First Row - Key Metrics */}
          <div className="grid grid-cols-5 gap-4 mb-4">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wide">Total Users</p>
                  <p className="text-2xl font-bold text-white">
                    {loadingStats ? '...' : quickStats?.users?.total || 0}
                  </p>
                  <p className="text-xs text-emerald-400 mt-1">
                    +{quickStats?.users?.new_today || 0} today
                  </p>
                </div>
                <Users className="w-8 h-8 text-[#74A57F]" />
              </div>
            </div>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wide">Active This Week</p>
                  <p className="text-2xl font-bold text-white">
                    {loadingStats ? '...' : quickStats?.users?.active_this_week || 0}
                  </p>
                  <p className="text-xs text-blue-400 mt-1">
                    +{quickStats?.users?.new_this_week || 0} new
                  </p>
                </div>
                <Activity className="w-8 h-8 text-blue-500" />
              </div>
            </div>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wide">Total Orgs</p>
                  <p className="text-2xl font-bold text-white">
                    {loadingStats ? '...' : quickStats?.organisations?.total || organisations.length}
                  </p>
                </div>
                <Building2 className="w-8 h-8 text-purple-500" />
              </div>
            </div>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wide">Entries</p>
                  <p className="text-2xl font-bold text-white">
                    {loadingStats ? '...' : (quickStats?.emissions?.total_entries || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-amber-400 mt-1">
                    +{quickStats?.emissions?.entries_this_week || 0} this week
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-amber-500" />
              </div>
            </div>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wide">Storage</p>
                  <p className="text-2xl font-bold text-white">
                    {loadingStats ? '...' : quickStats?.storage?.total_mb || 0} MB
                  </p>
                </div>
                <HardDrive className="w-8 h-8 text-slate-500" />
              </div>
            </div>
          </div>

          {/* Second Row - Subscription Breakdown */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-slate-800 rounded-xl p-3 border border-slate-700 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className="text-slate-300 text-sm">Pro</span>
              </div>
              <span className="text-xl font-bold text-emerald-400">
                {quickStats?.organisations?.by_tier?.pro || organisations.filter(o => o.subscription_status === 'pro').length}
              </span>
            </div>
            <div className="bg-slate-800 rounded-xl p-3 border border-slate-700 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-slate-300 text-sm">Standard</span>
              </div>
              <span className="text-xl font-bold text-blue-400">
                {quickStats?.organisations?.by_tier?.standard || organisations.filter(o => o.subscription_status === 'standard').length}
              </span>
            </div>
            <div className="bg-slate-800 rounded-xl p-3 border border-slate-700 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 rounded-full bg-slate-500" />
                <span className="text-slate-300 text-sm">Demo</span>
              </div>
              <span className="text-xl font-bold text-slate-400">
                {quickStats?.organisations?.by_tier?.demo || organisations.filter(o => !o.subscription_status || o.subscription_status === 'demo' || o.subscription_status === 'free').length}
              </span>
            </div>
            <div className="bg-slate-800 rounded-xl p-3 border border-slate-700 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Eye className="w-4 h-4 text-amber-500" />
                <span className="text-slate-300 text-sm">Support Active</span>
              </div>
              <span className="text-xl font-bold text-amber-400">
                {organisations.filter(o => o.support_access_active).length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setActiveTab('organisations')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'organisations'
              ? 'bg-[#74A57F] text-white'
              : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
          >
            <Building2 className="w-4 h-4 inline mr-2" />
            Organisations
          </button>
          <button
            onClick={() => { setActiveTab('admins'); fetchAdminAccounts(); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'admins'
              ? 'bg-purple-500 text-white'
              : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
          >
            <Shield className="w-4 h-4 inline mr-2" />
            Admin Accounts
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'audit'
              ? 'bg-[#74A57F] text-white'
              : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Audit Logs
          </button>
          <button
            onClick={() => { setActiveTab('support'); fetchSupportThreads(); fetchReplyTemplates(); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'support'
              ? 'bg-[#74A57F] text-white'
              : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
          >
            <MessageSquare className="w-4 h-4 inline mr-2" />
            Support
          </button>
          <button
            onClick={() => { setActiveTab('helpguides'); fetchHelpGuides(); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'helpguides'
              ? 'bg-[#74A57F] text-white'
              : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
          >
            <HelpCircle className="w-4 h-4 inline mr-2" />
            Help Guides
          </button>
          <button
            onClick={() => { setActiveTab('testaccounts'); fetchAllTestAccounts(); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'testaccounts'
              ? 'bg-amber-500 text-white'
              : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
          >
            <TestTube className="w-4 h-4 inline mr-2" />
            Test Accounts
          </button>
          <button
            onClick={() => { setActiveTab('factors'); fetchFactorYears(); fetchEmissionFactors(); fetchCategoryMappings(); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'factors'
              ? 'bg-emerald-500 text-white'
              : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            data-testid="factors-tab"
          >
            <Database className="w-4 h-4 inline mr-2" />
            Factor Manager
          </button>
          <button
            onClick={() => { setActiveTab('purges'); fetchPendingPurges(); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'purges'
              ? 'bg-red-500 text-white'
              : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            data-testid="purges-tab"
          >
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            Pending Purges
            {pendingPurges.length > 0 && (
              <span className="ml-2 bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">{pendingPurges.length}</span>
            )}
          </button>
          <button
            onClick={() => { setActiveTab('frozen'); fetchFrozenOrgs(); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'frozen'
              ? 'bg-cyan-500 text-white'
              : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            data-testid="frozen-tab"
          >
            <Clock className="w-4 h-4 inline mr-2" />
            Frozen Orgs
          </button>
          <button
            onClick={() => { setActiveTab('platform'); fetchPlatformLogo(); fetchPlatformSettings(); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'platform'
              ? 'bg-emerald-500 text-white'
              : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            data-testid="platform-settings-tab"
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Platform Settings
          </button>
          <button
            onClick={() => { setActiveTab('allusers'); fetchAllUsers(); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'allusers'
              ? 'bg-blue-500 text-white'
              : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            data-testid="users-tab"
          >
            <Users className="w-4 h-4 inline mr-2" />
            All Users
          </button>
          <button
            onClick={() => setActiveTab('roles')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'roles'
              ? 'bg-indigo-500 text-white'
              : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            data-testid="roles-tab"
          >
            <Table className="w-4 h-4 inline mr-2" />
            Roles Matrix
          </button>
        </div>

        {/* Admin Accounts Tab */}
        {activeTab === 'admins' && (
          <div className="space-y-6">
            {/* Admin Header */}
            <div className="bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border border-purple-500/30 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white text-lg font-bold">Admin Account Management</h3>
                    <p className="text-purple-200/70 text-sm">
                      {adminStatus?.email === SUPERADMIN_EMAIL
                        ? 'You can create both SuperAdmin and Admin accounts'
                        : 'You can create Admin accounts only'}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowCreateAdminModal(true)}
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                  data-testid="create-admin-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create Admin Account
                </Button>
              </div>

              <div className="bg-slate-800/50 rounded-lg p-4 text-sm text-slate-300">
                <p className="font-medium text-purple-300 mb-2">Role Hierarchy & Permissions:</p>
                <ul className="space-y-2 text-xs">
                  <li className="flex items-start">
                    <Crown className="w-3 h-3 mr-2 mt-0.5 text-amber-400 flex-shrink-0" />
                    <div>
                      <span className="text-amber-400 font-medium">SuperAdmin</span>
                      <span className="text-slate-400"> - Full system access. Can view all data, manage overrides, and create SuperAdmin & Admin accounts. Primary SuperAdmin cannot be deleted.</span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <Shield className="w-3 h-3 mr-2 mt-0.5 text-purple-400 flex-shrink-0" />
                    <div>
                      <span className="text-purple-400 font-medium">Admin</span>
                      <span className="text-slate-400"> - Can view all data, create test accounts, and manage overrides. Cannot create SuperAdmins.</span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* Admin Accounts List */}
            <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
              <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                <div>
                  <h3 className="text-white font-medium">Admin & SuperAdmin Accounts</h3>
                  <p className="text-slate-500 text-sm">{adminAccounts.length} accounts with elevated privileges</p>
                </div>
                <Button
                  onClick={fetchAdminAccounts}
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-white"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>

              <div className="divide-y divide-slate-700">
                {adminAccounts.length === 0 ? (
                  <div className="p-8 text-center">
                    <Shield className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-500">No admin accounts found</p>
                    <p className="text-slate-600 text-sm mt-1">Click "Create Admin Account" to add one</p>
                  </div>
                ) : (
                  adminAccounts.map((admin) => (
                    <div key={admin.id} className="p-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors">
                      <div className="flex items-center space-x-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${admin.role === 'superadmin' ? 'bg-amber-500/20' : 'bg-purple-500/20'
                          }`}>
                          {admin.role === 'superadmin' ? (
                            <Crown className="w-5 h-5 text-amber-400" />
                          ) : (
                            <Shield className="w-5 h-5 text-purple-400" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <p className="text-white font-medium">{admin.name}</p>
                            {admin.is_primary_superadmin && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/20 text-amber-400">
                                PRIMARY
                              </span>
                            )}
                          </div>
                          <p className="text-slate-400 text-sm font-mono">{admin.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`px-2.5 py-1 rounded text-xs font-medium ${admin.role === 'superadmin'
                          ? 'bg-amber-500/20 text-amber-400'
                          : 'bg-purple-500/20 text-purple-400'
                          }`}>
                          {admin.role === 'superadmin' ? 'SuperAdmin' : 'Admin'}
                        </span>
                        {!admin.has_supabase_auth && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-slate-700 text-slate-400" title="No Supabase auth linked">
                            LOCAL
                          </span>
                        )}
                        {!admin.is_primary_superadmin && adminStatus?.email === SUPERADMIN_EMAIL && (
                          <Button
                            onClick={() => deleteAdminAccount(admin.id, admin.email)}
                            disabled={deletingAdminId === admin.id}
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            {deletingAdminId === admin.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="p-4 bg-slate-700/30 border-t border-slate-700">
                <p className="text-slate-400 text-xs">
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  Removing admin privileges converts the account to a regular user. The primary SuperAdmin cannot be deleted.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Organisations Tab */}
        {activeTab === 'organisations' && (
          <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
            {/* Search */}
            <div className="p-4 border-b border-slate-700">
              <div className="flex items-center space-x-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name, email, or ABN..."
                    className="pl-10 bg-slate-900 border-slate-700 text-white placeholder-slate-500"
                  />
                </div>
                <Button
                  onClick={fetchOrganisations}
                  variant="outline"
                  className="border-slate-600 text-slate-300"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Organisation</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Owner</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">State</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Paid to date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Override</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Entries</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Support</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {filteredOrgs.map((org) => (
                    <tr key={org.id} className="hover:bg-slate-700/30 transition-colors">
                      <td className="px-4 py-4">
                        <div>
                          <p className="text-white font-medium">{org.name}</p>
                          <p className="text-xs text-slate-500">{org.abn || 'No ABN'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col items-start gap-1">
                          <div>
                            <p className="text-white font-medium text-sm">{org.owner?.name || 'Unknown'}</p>
                            <p className="text-slate-400 text-xs">{org.owner?.email || org.owner_email || 'No email'}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs text-blue-400 hover:text-blue-300 -ml-2"
                            onClick={() => {
                              setTransferModalOrg(org);
                              setTransferOwnerEmail('');
                            }}
                          >
                            Change Owner
                          </Button>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-slate-300">{org.state || '-'}</span>
                      </td>
                      {/* Status Column - Read-only, shows Stripe subscription status */}
                      <td className="px-4 py-4">
                        <div className="flex flex-col">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${org.subscription_status === 'pro'
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : org.subscription_status === 'standard'
                              ? 'bg-blue-500/20 text-blue-400'
                              : org.subscription_status === 'cancelled'
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-slate-600/50 text-slate-400'
                            }`}>
                            {org.subscription_status === 'pro' && <Crown className="w-3 h-3 mr-1" />}
                            {org.subscription_status === 'standard' && <Star className="w-3 h-3 mr-1" />}
                            {org.subscription_status === 'pro' ? 'Pro'
                              : org.subscription_status === 'standard' ? 'Standard'
                                : org.subscription_status === 'cancelled' ? 'Cancelled'
                                  : 'Demo'}
                          </span>
                          <span className="text-[10px] text-slate-500 mt-0.5">Stripe-managed</span>
                        </div>
                      </td>
                      {/* Paid to Date Column */}
                      <td className="px-4 py-4">
                        <span className="text-slate-300 text-sm">
                          {org.current_period_end ? new Date(org.current_period_end).toLocaleDateString() : '-'}
                        </span>
                      </td>
                      {/* Subscription Override Column */}
                      <td className="px-4 py-4">
                        <button
                          onClick={() => openOverrideModal(org)}
                          className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium cursor-pointer transition-all ${org.subscription_override === 'manual_pro'
                            ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30'
                            : org.subscription_override === 'manual_standard'
                              ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30'
                              : org.subscription_override === 'manual_demo' || org.subscription_override === 'manual_trial'
                                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30'
                                : 'bg-slate-700/50 text-slate-400 border border-slate-600 hover:bg-slate-700'
                            }`}
                          data-testid={`override-btn-${org.id}`}
                        >
                          {org.subscription_override === 'manual_pro' ? (
                            <>
                              <Crown className="w-3 h-3 mr-1" />
                              Manual Pro
                            </>
                          ) : org.subscription_override === 'manual_standard' ? (
                            <>
                              <Star className="w-3 h-3 mr-1" />
                              Manual Standard
                            </>
                          ) : org.subscription_override === 'manual_demo' || org.subscription_override === 'manual_trial' ? (
                            <>
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Manual Demo
                            </>
                          ) : (
                            <>
                              <Settings className="w-3 h-3 mr-1" />
                              System
                            </>
                          )}
                        </button>
                        {org.subscription_override_expiry && (
                          <p className="text-xs text-slate-500 mt-1">
                            Expires: {new Date(org.subscription_override_expiry).toLocaleDateString()}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-slate-300">{org.entry_count}</span>
                      </td>
                      <td className="px-4 py-4">
                        {org.support_access_active ? (
                          <span className="inline-flex items-center text-xs text-amber-400">
                            <Eye className="w-3 h-3 mr-1" />
                            Active
                          </span>
                        ) : (
                          <span className="text-slate-500 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewMembers(org)}
                            className="text-slate-400 hover:text-white"
                          >
                            <Users className="w-4 h-4 mr-1" />
                            Members
                          </Button>
                          <Button
                            onClick={() => setDeleteModalOrg(org)}
                            size="sm"
                            variant="destructive"
                            className="bg-red-600/20 hover:bg-red-600 text-red-500 hover:text-white border border-red-500/50 text-xs"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredOrgs.length === 0 && (
                <div className="text-center py-12">
                  <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-500">No organisations found</p>
                </div>
              )}
            </div>
          </div>
        )
        }

        {/* Audit Logs Tab */}
        {
          activeTab === 'audit' && (
            <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
              <div className="p-4 border-b border-slate-700">
                <h3 className="text-white font-medium">Recent Audit Events</h3>
                <p className="text-slate-500 text-sm">All system actions and admin activities</p>
              </div>

              <div className="divide-y divide-slate-700 max-h-[600px] overflow-y-auto">
                {auditLogs.map((log) => (
                  <div key={log.id} className="px-4 py-3 hover:bg-slate-700/30 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${log.is_admin_action ? 'bg-amber-500/20' : 'bg-slate-700'
                          }`}>
                          {log.action === 'create' && <Check className="w-4 h-4 text-emerald-400" />}
                          {log.action === 'update' && <RefreshCw className="w-4 h-4 text-blue-400" />}
                          {log.action === 'delete' && <X className="w-4 h-4 text-red-400" />}
                          {log.action === 'impersonate' && <Eye className="w-4 h-4 text-amber-400" />}
                          {log.action === 'export' && <FileText className="w-4 h-4 text-purple-400" />}
                          {!['create', 'update', 'delete', 'impersonate', 'export'].includes(log.action) &&
                            <Activity className="w-4 h-4 text-slate-400" />
                          }
                        </div>
                        <div>
                          <p className="text-white text-sm">
                            <span className="font-medium">{log.user_email || 'System'}</span>
                            {' '}
                            <span className="text-slate-400">{log.action}</span>
                            {' '}
                            <span className="text-slate-300">{log.resource_type}</span>
                          </p>
                          {log.description && (
                            <p className="text-slate-500 text-xs mt-0.5">{log.description}</p>
                          )}
                          {log.is_admin_action && (
                            <span className="inline-flex items-center text-xs text-amber-400 mt-1">
                              <Shield className="w-3 h-3 mr-1" />
                              Admin Action
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-slate-500 text-xs">
                          {log.created_at ? new Date(log.created_at).toLocaleString('en-AU') : '-'}
                        </p>
                        {log.ip_address && (
                          <p className="text-slate-600 text-xs">{log.ip_address}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {auditLogs.length === 0 && (
                  <div className="text-center py-12">
                    <Activity className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-500">No audit logs found</p>
                  </div>
                )}
              </div>
            </div>
          )
        }

        {/* Support Tab */}
        {
          activeTab === 'support' && (
            <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-3 h-[700px]">
                {/* Thread List */}
                <div className="border-r border-slate-700 flex flex-col">
                  <div className="p-4 border-b border-slate-700">
                    <h3 className="text-white font-medium">Support Inbox</h3>
                    <p className="text-slate-500 text-sm">Customer enquiries</p>

                    {/* Filters */}
                    <div className="mt-3">
                      <Select value={supportStatusFilter} onValueChange={(val) => { setSupportStatusFilter(val); setTimeout(fetchSupportThreads, 100); }}>
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                          <SelectValue placeholder="Filter by status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Threads</SelectItem>
                          <SelectItem value="open">Open Only</SelectItem>
                          <SelectItem value="closed">Closed Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto">
                    {loadingSupport ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                      </div>
                    ) : supportThreads.length === 0 ? (
                      <div className="p-8 text-center">
                        <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                        <p className="text-slate-500">No support threads</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-700">
                        {supportThreads.map((thread) => (
                          <button
                            key={thread.id}
                            onClick={() => { setSelectedSupportThread(thread); fetchSupportMessages(thread.id); }}
                            className={`w-full p-4 text-left hover:bg-slate-700/50 transition-colors ${selectedSupportThread?.id === thread.id ? 'bg-slate-700' : ''
                              }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2">
                                  <span className="text-white font-medium text-sm truncate">
                                    {thread.subject}
                                  </span>
                                  {thread.unread_count > 0 && (
                                    <span className="bg-red-500 text-white text-xs rounded-full px-1.5 min-w-[18px] text-center">
                                      {thread.unread_count}
                                    </span>
                                  )}
                                </div>
                                <p className="text-slate-400 text-xs mt-0.5">{thread.organisations?.name}</p>
                                <div className="flex items-center space-x-2 mt-1">
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${thread.category === 'technical_issue' ? 'bg-red-500/20 text-red-400' :
                                    thread.category === 'billing_query' ? 'bg-amber-500/20 text-amber-400' :
                                      thread.category === 'feature_request' ? 'bg-purple-500/20 text-purple-400' :
                                        'bg-blue-500/20 text-blue-400'
                                    }`}>
                                    {thread.category?.replace('_', ' ')}
                                  </span>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${thread.status === 'open' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600 text-slate-400'
                                    }`}>
                                    {thread.status}
                                  </span>
                                </div>
                              </div>
                              <span className="text-slate-500 text-xs ml-2">
                                {new Date(thread.last_message_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Conversation Panel */}
                <div className="col-span-2 flex flex-col">
                  {selectedSupportThread ? (
                    <>
                      {/* Thread Header */}
                      <div className="p-4 border-b border-slate-700 bg-slate-700/30">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="text-white font-medium">{selectedSupportThread.subject}</h3>
                            <p className="text-slate-400 text-sm">{selectedSupportThread.organisations?.name}</p>
                          </div>
                          <div className="flex space-x-2">
                            {selectedSupportThread.status === 'open' ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCloseThread(selectedSupportThread.id)}
                                className="border-slate-600 text-slate-300 hover:bg-slate-700"
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Close
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReopenThread(selectedSupportThread.id)}
                                className="border-slate-600 text-slate-300 hover:bg-slate-700"
                              >
                                <RefreshCw className="w-4 h-4 mr-1" />
                                Reopen
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Messages */}
                      <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {loadingSupportMessages ? (
                          <div className="flex justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                          </div>
                        ) : (
                          supportMessages.map((msg) => (
                            <div
                              key={msg.id}
                              className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                            >
                              <div className={`max-w-[75%] rounded-lg p-3 ${msg.sender_type === 'admin'
                                ? 'bg-[#74A57F] text-white'
                                : 'bg-slate-700 text-white'
                                }`}>
                                <p className="whitespace-pre-wrap text-sm">{msg.body}</p>

                                {msg.attachment_urls && msg.attachment_urls.length > 0 && (
                                  <div className="mt-2 space-y-1 pt-2 border-t border-white/20">
                                    {msg.attachment_urls.map((att, idx) => (
                                      <a
                                        key={idx}
                                        href={att.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center text-xs text-white/80 hover:text-white"
                                      >
                                        <FileText className="w-3 h-3 mr-1" />
                                        {att.name}
                                      </a>
                                    ))}
                                  </div>
                                )}

                                <p className="text-xs mt-2 opacity-60">
                                  {msg.sender_type === 'admin' ? 'You' : (msg.sender?.name || msg.sender?.email || 'Customer')} • {new Date(msg.created_at).toLocaleString('en-AU')}
                                </p>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Reply Area */}
                      <div className="p-4 border-t border-slate-700 bg-slate-700/30">
                        {/* Quick Reply Templates */}
                        {replyTemplates.length > 0 && (
                          <div className="mb-3">
                            <label className="text-xs text-slate-400 mb-2 block">Quick Reply Templates</label>
                            <div className="flex flex-wrap gap-2">
                              {replyTemplates.map((template) => (
                                <button
                                  key={template.id}
                                  onClick={() => setSupportReply(template.body)}
                                  className="text-xs px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded transition-colors"
                                >
                                  {template.title}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex items-end space-x-3">
                          <textarea
                            value={supportReply}
                            onChange={(e) => setSupportReply(e.target.value)}
                            placeholder="Type your reply..."
                            className="flex-1 bg-slate-700 border border-slate-600 rounded-lg p-3 text-white placeholder-slate-400 resize-none text-sm"
                            rows={3}
                          />
                          <Button
                            onClick={handleSendSupportReply}
                            disabled={sendingSupportReply || !supportReply.trim()}
                            className="bg-[#74A57F] hover:bg-[#5F8575] text-white"
                          >
                            {sendingSupportReply ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-2" />
                                Send & Notify
                              </>
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-slate-500 mt-2">
                          Customer will receive an email notification when you reply.
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="text-center">
                        <MessageSquare className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                        <h3 className="text-slate-400 font-medium mb-2">Select a thread</h3>
                        <p className="text-slate-500 text-sm">Choose a support thread from the list</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        }

        {/* Help Guides Tab */}
        {
          activeTab === 'helpguides' && (
            <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
              <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                <div>
                  <h3 className="text-white font-medium">Help Guide Editor</h3>
                  <p className="text-slate-500 text-sm">Manage help center content for users</p>
                </div>
                <Button
                  onClick={saveHelpGuides}
                  disabled={savingGuides}
                  className="bg-[#74A57F] hover:bg-[#5F8575] text-white"
                >
                  {savingGuides ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save All Changes
                    </>
                  )}
                </Button>
              </div>

              {loadingGuides ? (
                <div className="p-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-[#74A57F] mx-auto" />
                  <p className="text-slate-500 mt-4">Loading help guides...</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-700 max-h-[600px] overflow-y-auto">
                  {helpGuides.map((guide) => (
                    <div key={guide.key} className="p-4">
                      <div
                        className="flex items-center justify-between cursor-pointer mb-3"
                        onClick={() => setEditingGuide(editingGuide === guide.key ? null : guide.key)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-[#74A57F]/20 rounded-lg flex items-center justify-center">
                            <HelpCircle className="w-5 h-5 text-[#74A57F]" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{guide.title}</p>
                            <p className="text-slate-500 text-sm">{guide.items?.length || 0} items</p>
                          </div>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${editingGuide === guide.key ? 'rotate-180' : ''
                          }`} />
                      </div>

                      {editingGuide === guide.key && (
                        <div className="ml-13 space-y-4 mt-4 pl-12">
                          {guide.items?.map((item, itemIndex) => (
                            <div key={item.id || itemIndex} className="bg-slate-700/50 rounded-lg p-4">
                              <div className="flex items-start justify-between mb-2">
                                <label className="text-slate-400 text-xs uppercase tracking-wide">Question</label>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeGuideItem(guide.key, itemIndex)}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-6 w-6 p-0"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                              <Input
                                value={item.question}
                                onChange={(e) => updateGuideItem(guide.key, itemIndex, 'question', e.target.value)}
                                className="bg-slate-800 border-slate-600 text-white mb-3"
                              />
                              <label className="text-slate-400 text-xs uppercase tracking-wide">Answer</label>
                              <textarea
                                value={item.answer}
                                onChange={(e) => updateGuideItem(guide.key, itemIndex, 'answer', e.target.value)}
                                className="w-full mt-1 bg-slate-800 border border-slate-600 text-white rounded-lg p-3 min-h-[80px] text-sm"
                              />
                            </div>
                          ))}

                          {/* Add new item */}
                          <div className="bg-slate-700/30 border-2 border-dashed border-slate-600 rounded-lg p-4">
                            <p className="text-slate-400 text-sm font-medium mb-3">
                              <Plus className="w-4 h-4 inline mr-1" />
                              Add New Item
                            </p>
                            <Input
                              placeholder="Question"
                              value={newItemText.question}
                              onChange={(e) => setNewItemText({ ...newItemText, question: e.target.value })}
                              className="bg-slate-800 border-slate-600 text-white mb-2"
                            />
                            <textarea
                              placeholder="Answer"
                              value={newItemText.answer}
                              onChange={(e) => setNewItemText({ ...newItemText, answer: e.target.value })}
                              className="w-full bg-slate-800 border border-slate-600 text-white rounded-lg p-3 min-h-[60px] text-sm mb-2"
                            />
                            <Button
                              onClick={() => addGuideItem(guide.key)}
                              variant="outline"
                              size="sm"
                              className="border-[#74A57F] text-[#74A57F] hover:bg-[#74A57F] hover:text-white"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add Item
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {helpGuides.length === 0 && (
                    <div className="text-center py-12">
                      <HelpCircle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                      <p className="text-slate-500">No help guides found</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        }

        {/* Test Accounts Tab */}
        {
          activeTab === 'testaccounts' && (
            <div className="space-y-6">
              {/* Test Mode Banner */}
              <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
                      <TestTube className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white text-lg font-bold">Test Account Management</h3>
                      <p className="text-amber-200/70 text-sm">Create and manage test accounts for QA and demos</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => setShowCreateAccountModal(true)}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                    data-testid="create-test-account-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Create Test Account
                  </Button>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-4 text-sm">
                  <p className="text-amber-300 font-medium mb-2">How Test Accounts Work:</p>
                  <ul className="text-slate-300 text-xs space-y-1">
                    <li>• Test accounts have <span className="text-slate-400">Demo</span> as their Stripe status (no payment)</li>
                    <li>• An <span className="text-purple-400">Override</span> grants Pro/Standard access without charge</li>
                    <li>• Override can be set to expire (auto-reverts to Demo)</li>
                  </ul>
                </div>
              </div>

              {/* All User Accounts */}
              <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
                <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-medium">All User Accounts</h3>
                    <p className="text-slate-500 text-sm">{allTestAccounts.length} accounts found</p>
                  </div>
                  <Button
                    onClick={fetchAllTestAccounts}
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-white"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>

                <div className="divide-y divide-slate-700 max-h-[500px] overflow-y-auto">
                  {allTestAccounts.length === 0 ? (
                    <div className="p-8 text-center">
                      <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                      <p className="text-slate-500">No user accounts found</p>
                      <p className="text-slate-600 text-sm mt-1">Click "Create Test Account" to add one</p>
                    </div>
                  ) : (
                    allTestAccounts.map((account) => (
                      <div key={account.id} className="p-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors">
                        <div className="flex items-center space-x-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${account.organisation?.effective_tier === 'pro' ? 'bg-emerald-500/20'
                            : account.organisation?.effective_tier === 'standard' ? 'bg-blue-500/20'
                              : 'bg-slate-700'
                            }`}>
                            <Users className={`w-5 h-5 ${account.organisation?.effective_tier === 'pro' ? 'text-emerald-400'
                              : account.organisation?.effective_tier === 'standard' ? 'text-blue-400'
                                : 'text-slate-500'
                              }`} />
                          </div>
                          <div>
                            <p className="text-white font-medium">{account.name}</p>
                            <p className="text-slate-400 text-sm font-mono">{account.email}</p>
                            {account.organisation && (
                              <p className="text-slate-500 text-xs mt-1">
                                Org: {account.organisation.name} • {account.org_role || 'member'}
                              </p>
                            )}
                            {/* Password Display */}
                            <div className="flex items-center mt-2 space-x-2">
                              {account.test_password ? (
                                <>
                                  <div className="flex items-center bg-slate-700 rounded px-2 py-1">
                                    <span className="text-xs font-mono text-slate-300">
                                      {revealedPasswords[account.id] ? account.test_password : '••••••••'}
                                    </span>
                                    <button
                                      onClick={() => togglePasswordReveal(account.id)}
                                      className="ml-2 text-slate-400 hover:text-white"
                                      title={revealedPasswords[account.id] ? 'Hide password' : 'Show password'}
                                    >
                                      {revealedPasswords[account.id] ? (
                                        <EyeOff className="w-3 h-3" />
                                      ) : (
                                        <Eye className="w-3 h-3" />
                                      )}
                                    </button>
                                  </div>
                                  <button
                                    onClick={() => resetTestAccountPassword(account.id, account.email)}
                                    disabled={resettingPasswordId === account.id}
                                    className="text-xs text-amber-400 hover:text-amber-300 flex items-center"
                                    title="Reset password"
                                  >
                                    {resettingPasswordId === account.id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <>
                                        <Key className="w-3 h-3 mr-1" />
                                        Reset
                                      </>
                                    )}
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => setTestAccountPassword(account.id, account.email)}
                                  className="text-xs text-blue-400 hover:text-blue-300 flex items-center bg-slate-700 rounded px-2 py-1"
                                  title="Set password for reveal feature"
                                >
                                  <Key className="w-3 h-3 mr-1" />
                                  Set Password
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          {account.organisation ? (
                            <div className="flex flex-col items-end space-y-1">
                              {/* Effective Tier Badge */}
                              <span className={`px-2 py-1 rounded text-xs font-medium ${account.organisation.effective_tier === 'pro'
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : account.organisation.effective_tier === 'standard'
                                  ? 'bg-blue-500/20 text-blue-400'
                                  : 'bg-slate-700 text-slate-400'
                                }`}>
                                {account.organisation.effective_tier?.toUpperCase() || 'DEMO'}
                              </span>
                              {/* Override indicator */}
                              {account.organisation.subscription_override && (
                                <span className="text-[10px] text-purple-400">
                                  via {account.organisation.subscription_override.type?.replace('manual_', '')} override
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="px-2 py-1 rounded text-xs font-medium bg-red-500/20 text-red-400">
                              NO ORG
                            </span>
                          )}
                          {!account.has_supabase_auth && (
                            <span className="px-2 py-1 rounded text-xs font-medium bg-amber-500/20 text-amber-400" title="No Supabase auth linked">
                              LOCAL
                            </span>
                          )}
                          <Button
                            onClick={() => {
                              const orgRoles = account.organisation ? [{
                                org_id: account.organisation.id,
                                org_name: account.organisation.name,
                                role: account.org_role || 'member'
                              }] : [];
                              setOrgManageModalUser({ ...account, orgRoles });
                              if (organisations.length === 0) fetchOrganisations();
                            }}
                            variant="ghost"
                            size="sm"
                            className="text-slate-400 hover:text-white"
                            title="Manage Organisations"
                          >
                            <Building2 className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => {
                              setEditUserModal(account);
                              setEditUserName(account.name || '');
                            }}
                            variant="ghost"
                            size="sm"
                            className="text-slate-400 hover:text-white"
                            title="Edit User Name"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => deleteTestAccount(account.id, account.email)}
                            disabled={deletingAccountId === account.id}
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            {deletingAccountId === account.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="p-4 bg-slate-700/30 border-t border-slate-700">
                  <p className="text-slate-400 text-xs">
                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                    Deleting an account removes all associated organisations and emissions data. This cannot be undone.
                  </p>
                </div>
              </div>
            </div>
          )
        }
        {/* Create Test Account Modal */}
        {
          showCreateAccountModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-slate-800 rounded-2xl border border-slate-700 w-full max-w-md p-6 mx-4">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-white">Create Test Account</h3>
                  <Button
                    onClick={() => setShowCreateAccountModal(false)}
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Name</label>
                    <Input
                      value={newAccountData.name}
                      onChange={(e) => setNewAccountData({ ...newAccountData, name: e.target.value })}
                      placeholder="John Doe"
                      className="bg-slate-900 border-slate-700 text-white"
                    />
                  </div>

                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Email</label>
                    <Input
                      type="email"
                      value={newAccountData.email}
                      onChange={(e) => setNewAccountData({ ...newAccountData, email: e.target.value })}
                      placeholder="test@example.com"
                      className="bg-slate-900 border-slate-700 text-white"
                    />
                  </div>

                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Password</label>
                    <Input
                      type="text"
                      value={newAccountData.password}
                      onChange={(e) => setNewAccountData({ ...newAccountData, password: e.target.value })}
                      placeholder="SecurePassword123!"
                      className="bg-slate-900 border-slate-700 text-white"
                    />
                  </div>

                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Subscription Tier</label>
                    <Select
                      value={newAccountData.subscription_tier}
                      onValueChange={(value) => setNewAccountData({ ...newAccountData, subscription_tier: value })}
                    >
                      <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="demo" className="text-slate-300 hover:bg-slate-700">Demo (Free)</SelectItem>
                        <SelectItem value="standard" className="text-blue-400 hover:bg-slate-700">Standard ($11/mo)</SelectItem>
                        <SelectItem value="pro" className="text-emerald-400 hover:bg-slate-700">Pro ($22/mo)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <Button
                    onClick={() => setShowCreateAccountModal(false)}
                    variant="outline"
                    className="border-slate-600 text-slate-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={createTestAccount}
                    disabled={creatingTestAccounts}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  >
                    {creatingTestAccounts ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4 mr-2" />
                        Create Account
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )
        }

        {/* Factor Manager Tab */}
        {
          activeTab === 'factors' && (
            <div className="space-y-6">
              {/* Header with Year Selector */}
              <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-white flex items-center">
                      <Database className="w-5 h-5 mr-2 text-emerald-400" />
                      Emission Factors Manager
                    </h3>
                    <p className="text-slate-400 text-sm mt-1">
                      Manage National Greenhouse Account factors. Changes won't affect existing entries.
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Select
                      value={selectedFactorYear.toString()}
                      onValueChange={(val) => { setSelectedFactorYear(parseInt(val)); fetchEmissionFactors(parseInt(val)); }}
                    >
                      <SelectTrigger className="w-48 bg-slate-900 border-slate-700 text-white">
                        <SelectValue placeholder="Select FY" />
                      </SelectTrigger>
                      <SelectContent>
                        {(factorYears.length > 0 ? factorYears : [2026]).map(year => (
                          <SelectItem key={year} value={year.toString()}>
                            FY {year - 1}-{year.toString().slice(-2)} (NGA {year - 1})
                          </SelectItem>
                        ))}
                        <SelectItem value="2027">FY 2026-27 (NGA 2026) - New</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={() => setShowAddFactor(true)}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white"
                      data-testid="add-factor-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Factor
                    </Button>
                  </div>
                </div>

                {/* Versioning Notice */}
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-300 text-sm">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  <strong>Factor Versioning:</strong> Each emission entry stores the factor used at creation time.
                  Updating factors here only affects <strong>new entries</strong>. Historical data remains unchanged for audit compliance.
                </div>

                {/* Naming Convention Help */}
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-blue-300 text-sm">
                  <HelpCircle className="w-4 h-4 inline mr-2" />
                  <strong>Naming Convention:</strong> "FY 2025-26 (NGA 2025)" means factors from the NGA Factors 2025 publication (released July 2025)
                  are used for calculating emissions in Financial Year 2025-26 (July 2025 - June 2026).
                </div>
              </div>

              {/* Add New Factor Form */}
              {showAddFactor && (
                <div className="bg-slate-800 rounded-2xl border border-emerald-500/50 p-6">
                  <h4 className="text-lg font-semibold text-white mb-4">Add New Emission Factor</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Year *</label>
                      <Input
                        type="number"
                        value={newFactor.factor_year}
                        onChange={(e) => setNewFactor({ ...newFactor, factor_year: parseInt(e.target.value) })}
                        className="bg-slate-900 border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Category *</label>
                      <Input
                        placeholder="e.g., electricity, fuel_petrol"
                        value={newFactor.category}
                        onChange={(e) => setNewFactor({ ...newFactor, category: e.target.value })}
                        className="bg-slate-900 border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">State (or empty for National)</label>
                      <Select
                        value={newFactor.state}
                        onValueChange={(val) => setNewFactor({ ...newFactor, state: val === 'national' ? '' : val })}
                      >
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                          <SelectValue placeholder="National" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="national">National (NAT)</SelectItem>
                          <SelectItem value="NSW">NSW</SelectItem>
                          <SelectItem value="VIC">VIC</SelectItem>
                          <SelectItem value="QLD">QLD</SelectItem>
                          <SelectItem value="WA">WA</SelectItem>
                          <SelectItem value="SA">SA</SelectItem>
                          <SelectItem value="TAS">TAS</SelectItem>
                          <SelectItem value="ACT">ACT</SelectItem>
                          <SelectItem value="NT">NT</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Factor Value *</label>
                      <Input
                        type="number"
                        step="0.000001"
                        placeholder="e.g., 0.00067"
                        value={newFactor.factor_value}
                        onChange={(e) => setNewFactor({ ...newFactor, factor_value: e.target.value })}
                        className="bg-slate-900 border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Unit</label>
                      <Select
                        value={newFactor.factor_unit}
                        onValueChange={(val) => setNewFactor({ ...newFactor, factor_unit: val })}
                      >
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="kWh">kWh</SelectItem>
                          <SelectItem value="L">Litres (L)</SelectItem>
                          <SelectItem value="GJ">GJ</SelectItem>
                          <SelectItem value="tonnes">Tonnes</SelectItem>
                          <SelectItem value="kL">kL</SelectItem>
                          <SelectItem value="pax-km">pax-km</SelectItem>
                          <SelectItem value="nights">Nights</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Scope</label>
                      <Select
                        value={newFactor.scope}
                        onValueChange={(val) => setNewFactor({ ...newFactor, scope: val })}
                      >
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Scope 1">Scope 1 (Direct)</SelectItem>
                          <SelectItem value="Scope 2">Scope 2 (Purchased Energy)</SelectItem>
                          <SelectItem value="Scope 3">Scope 3 (Indirect)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">NGA Source Edition</label>
                      <Select
                        value={newFactor.nga_source_edition}
                        onValueChange={(val) => setNewFactor({ ...newFactor, nga_source_edition: val })}
                      >
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                          <SelectValue placeholder="Select source" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="NGA Factors 2021">NGA Factors 2021</SelectItem>
                          <SelectItem value="NGA Factors 2022">NGA Factors 2022</SelectItem>
                          <SelectItem value="NGA Factors 2023">NGA Factors 2023</SelectItem>
                          <SelectItem value="NGA Factors 2024">NGA Factors 2024</SelectItem>
                          <SelectItem value="NGA Factors 2025">NGA Factors 2025</SelectItem>
                          <SelectItem value="NGA Factors 2026">NGA Factors 2026</SelectItem>
                          <SelectItem value="DEFRA 2024">DEFRA UK 2024</SelectItem>
                          <SelectItem value="DEFRA 2025">DEFRA UK 2025</SelectItem>
                          <SelectItem value="ICAO 2024">ICAO 2024</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Notes</label>
                      <Input
                        placeholder="e.g., NSW electricity grid factor"
                        value={newFactor.notes}
                        onChange={(e) => setNewFactor({ ...newFactor, notes: e.target.value })}
                        className="bg-slate-900 border-slate-700 text-white"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3 mt-4">
                    <Button
                      onClick={() => setShowAddFactor(false)}
                      variant="outline"
                      className="border-slate-600 text-slate-300"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={createEmissionFactor}
                      disabled={savingFactor}
                      className="bg-emerald-500 hover:bg-emerald-600"
                    >
                      {savingFactor ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Create Factor
                    </Button>
                  </div>
                </div>
              )}


              {/* Category Display Mapping */}
              <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
                <div className="p-4 border-b border-slate-700 flex justify-between items-center bg-slate-700/30">
                  <div>
                    <h4 className="font-bold text-white flex items-center">
                      <Settings className="w-4 h-4 mr-2" />
                      Category Display Mapping
                    </h4>
                    <p className="text-slate-400 text-xs">
                      Select which categories appear in the front-end GUI dropdowns (Dashboard, Entries, CSV).
                    </p>
                  </div>
                  <Button
                    onClick={fetchCategoryMappings}
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-white"
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingMappings ? 'animate-spin' : ''}`} />
                  </Button>
                </div>

                <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[400px] overflow-y-auto">
                  {loadingMappings ? (
                    <div className="col-span-full py-8 text-center text-slate-500">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Loading mappings...
                    </div>
                  ) : categoryMappings.length === 0 ? (
                    <div className="col-span-full py-8 text-center text-slate-500">
                      No category mappings found in database.
                    </div>
                  ) : (
                    categoryMappings.map((mapping) => (
                      <div
                        key={mapping.category_key}
                        className={`p-3 rounded-lg border transition-all flex items-center justify-between ${mapping.is_visible
                            ? 'bg-emerald-500/10 border-emerald-500/30'
                            : 'bg-slate-900 border-slate-700 opacity-60'
                          }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg ${mapping.is_visible ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800 text-slate-500'}`}>
                            <Database className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="text-white text-sm font-medium">{mapping.label}</p>
                            <p className="text-slate-500 text-[10px] uppercase font-bold tracking-wider">{mapping.scope} • {mapping.unit}</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={updatingMapping === mapping.category_key}
                          onClick={() => toggleCategoryVisibility(mapping.category_key, mapping.is_visible)}
                          className={mapping.is_visible ? 'text-emerald-400 hover:text-emerald-300' : 'text-slate-400 hover:text-white'}
                        >
                          {updatingMapping === mapping.category_key ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : mapping.is_visible ? (
                            <Eye className="w-4 h-4" />
                          ) : (
                            <EyeOff className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>


              {/* Factors Table */}
              <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
                <div className="p-4 border-b border-slate-700 flex justify-between items-center">
                  <h4 className="font-medium text-white">
                    FY {selectedFactorYear - 1}-{selectedFactorYear.toString().slice(-2)} Factors ({emissionFactors.length})
                  </h4>
                  <Button
                    onClick={() => fetchEmissionFactors(selectedFactorYear)}
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-white"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>

                {loadingFactors ? (
                  <div className="p-8 text-center text-slate-400">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                    Loading factors...
                  </div>
                ) : emissionFactors.length === 0 ? (
                  <div className="p-8 text-center text-slate-400">
                    No factors found for FY {selectedFactorYear - 1}-{selectedFactorYear.toString().slice(-2)}. Add the first factor above.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-900">
                        <tr className="text-slate-400 text-xs uppercase">
                          <th className="text-left py-3 px-4">Category</th>
                          <th className="text-left py-3 px-4">Region</th>
                          <th className="text-left py-3 px-4">Scope</th>
                          <th className="text-right py-3 px-4">Factor Value</th>
                          <th className="text-left py-3 px-4">Unit</th>
                          <th className="text-left py-3 px-4">NGA Source</th>
                          <th className="text-left py-3 px-4">Notes</th>
                          <th className="text-center py-3 px-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700">
                        {emissionFactors.map((factor) => (
                          <tr key={factor.id} className="hover:bg-slate-700/50">
                            <td className="py-3 px-4 text-white font-medium">{factor.category}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-0.5 rounded text-xs ${factor.state
                                ? 'bg-blue-500/20 text-blue-300'
                                : 'bg-slate-700 text-slate-400'
                                }`}>
                                {factor.state || 'National'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-0.5 rounded text-xs ${factor.scope === 'Scope 1' ? 'bg-orange-500/20 text-orange-300' :
                                factor.scope === 'Scope 2' ? 'bg-amber-500/20 text-amber-300' :
                                  'bg-emerald-500/20 text-emerald-300'
                                }`}>
                                {factor.scope}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              {editingFactor === factor.id ? (
                                <Input
                                  type="number"
                                  step="0.000001"
                                  defaultValue={factor.factor_value}
                                  className="w-28 bg-slate-900 border-slate-600 text-white text-sm"
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      updateEmissionFactor(factor.id, parseFloat(e.target.value));
                                    } else if (e.key === 'Escape') {
                                      setEditingFactor(null);
                                    }
                                  }}
                                  autoFocus
                                />
                              ) : (
                                <span className="text-emerald-400 font-mono">{factor.factor_value.toFixed(6)}</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-slate-400 text-sm">{factor.factor_unit}</td>
                            <td className="py-3 px-4">
                              {factor.nga_source_edition ? (
                                <span className="px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-300">
                                  {factor.nga_source_edition}
                                </span>
                              ) : (
                                <span className="text-slate-500 text-xs">—</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-slate-400 text-sm max-w-[150px] truncate" title={factor.notes}>{factor.notes}</td>
                            <td className="py-3 px-4 text-center">
                              <div className="flex items-center justify-center space-x-2">
                                <Button
                                  onClick={() => setEditingFactor(editingFactor === factor.id ? null : factor.id)}
                                  variant="ghost"
                                  size="sm"
                                  className="text-slate-400 hover:text-white h-8 w-8 p-0"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  onClick={() => deleteEmissionFactor(factor.id)}
                                  variant="ghost"
                                  size="sm"
                                  className="text-slate-400 hover:text-red-400 h-8 w-8 p-0"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )
        }

        {/* Pending Purges Tab */}
        {
          activeTab === 'purges' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/30 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white text-lg font-bold">Pending Data Purges</h3>
                      <p className="text-red-200/70 text-sm">
                        Organisations scheduled for data deletion after subscription cancellation
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={fetchPendingPurges}
                    variant="ghost"
                    className="text-red-300 hover:text-white"
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingPurges ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>

              {loadingPurges ? (
                <div className="bg-slate-800 rounded-2xl p-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-red-400" />
                  <p className="text-slate-400">Loading pending purges...</p>
                </div>
              ) : pendingPurges.length === 0 ? (
                <div className="bg-slate-800 rounded-2xl p-8 text-center border border-slate-700">
                  <Check className="w-12 h-12 mx-auto mb-4 text-green-400" />
                  <p className="text-white font-medium">No Pending Purges</p>
                  <p className="text-slate-400 text-sm mt-1">All organisations are in good standing</p>
                </div>
              ) : (
                <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-900">
                      <tr className="text-slate-400 text-xs uppercase">
                        <th className="text-left py-3 px-4">Organisation</th>
                        <th className="text-left py-3 px-4">Owner</th>
                        <th className="text-center py-3 px-4">Days Left</th>
                        <th className="text-left py-3 px-4">Purge Date</th>
                        <th className="text-right py-3 px-4">Data</th>
                        <th className="text-center py-3 px-4">Warnings</th>
                        <th className="text-center py-3 px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {pendingPurges.map((purge) => (
                        <tr key={purge.id} className="hover:bg-slate-700/50">
                          <td className="py-3 px-4">
                            <span className="text-white font-medium">{purge.name}</span>
                          </td>
                          <td className="py-3 px-4 text-slate-300 text-sm">{purge.owner_email}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${purge.days_remaining <= 1 ? 'bg-red-500/20 text-red-400' :
                              purge.days_remaining <= 7 ? 'bg-orange-500/20 text-orange-400' :
                                'bg-amber-500/20 text-amber-400'
                              }`}>
                              {purge.days_remaining} days
                            </span>
                          </td>
                          <td className="py-3 px-4 text-slate-300 text-sm">
                            {purge.purge_scheduled_at ? new Date(purge.purge_scheduled_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="text-slate-300 text-sm">
                              {purge.entry_count} entries<br />
                              <span className="text-slate-500">{purge.storage_mb} MB</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex justify-center space-x-1">
                              <span className={`w-2 h-2 rounded-full ${purge.warning_sent ? 'bg-green-400' : 'bg-slate-600'}`} title="15-day warning" />
                              <span className={`w-2 h-2 rounded-full ${purge.final_notice_sent ? 'bg-green-400' : 'bg-slate-600'}`} title="24-hour notice" />
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex justify-center space-x-2">
                              <Button
                                onClick={() => cancelPurge(purge.id, purge.name)}
                                size="sm"
                                variant="ghost"
                                className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                title="Cancel Purge"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => executePurge(purge.id, purge.name)}
                                size="sm"
                                variant="ghost"
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                title="Execute Purge Now"
                                disabled={executingPurge === purge.id}
                              >
                                {executingPurge === purge.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )
        }

        {/* Frozen Orgs Tab */}
        {
          activeTab === 'frozen' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white text-lg font-bold">Frozen Organisations</h3>
                      <p className="text-cyan-200/70 text-sm">
                        Organisations with read-only access (paying $4/month freeze fee, storage &lt;10GB)
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={fetchFrozenOrgs}
                    variant="ghost"
                    className="text-cyan-300 hover:text-white"
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingFrozen ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>

              {loadingFrozen ? (
                <div className="bg-slate-800 rounded-2xl p-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-cyan-400" />
                  <p className="text-slate-400">Loading frozen organisations...</p>
                </div>
              ) : frozenOrgs.length === 0 ? (
                <div className="bg-slate-800 rounded-2xl p-8 text-center border border-slate-700">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-slate-500" />
                  <p className="text-white font-medium">No Frozen Organisations</p>
                  <p className="text-slate-400 text-sm mt-1">No organisations are currently in frozen state</p>
                </div>
              ) : (
                <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-900">
                      <tr className="text-slate-400 text-xs uppercase">
                        <th className="text-left py-3 px-4">Organisation</th>
                        <th className="text-left py-3 px-4">Owner</th>
                        <th className="text-left py-3 px-4">Frozen Since</th>
                        <th className="text-left py-3 px-4">Frozen Until</th>
                        <th className="text-right py-3 px-4">Entries</th>
                        <th className="text-center py-3 px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {frozenOrgs.map((org) => (
                        <tr key={org.id} className="hover:bg-slate-700/50">
                          <td className="py-3 px-4">
                            <span className="text-white font-medium">{org.name}</span>
                          </td>
                          <td className="py-3 px-4 text-slate-300 text-sm">{org.owner_email}</td>
                          <td className="py-3 px-4 text-slate-300 text-sm">
                            {org.frozen_at ? new Date(org.frozen_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="py-3 px-4">
                            {org.frozen_until ? (
                              <span className="text-cyan-300">
                                {new Date(org.frozen_until).toLocaleDateString()}
                                <span className="text-slate-500 text-xs ml-1">({org.days_remaining} days)</span>
                              </span>
                            ) : (
                              <span className="text-slate-500">Indefinite</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right text-slate-300">{org.entry_count}</td>
                          <td className="py-3 px-4 text-center">
                            <Button
                              onClick={() => {
                                const orgData = organisations.find(o => o.id === org.id);
                                if (orgData) {
                                  setOverrideModalOrg(orgData);
                                  setOverrideType(orgData.subscription_override || 'system_default');
                                }
                              }}
                              size="sm"
                              variant="ghost"
                              className="text-cyan-400 hover:text-cyan-300"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )
        }

        {/* Platform Settings Tab */}
        {
          activeTab === 'platform' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-2xl p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
                    <Settings className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white text-lg font-bold">Platform Settings</h3>
                    <p className="text-emerald-200/70 text-sm">
                      Configure global platform branding and settings
                    </p>
                  </div>
                </div>
              </div>

              {/* Platform Logo Section */}
              <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
                <h4 className="text-white font-semibold mb-4 flex items-center">
                  <Star className="w-5 h-5 mr-2 text-emerald-400" />
                  Platform Logo
                </h4>
                <p className="text-slate-400 text-sm mb-6">
                  This logo appears in the dashboard header and on all generated PDF reports (in the "Powered by" footer section).
                </p>

                <div className="flex items-start space-x-6">
                  {/* Current Logo Preview */}
                  <div className="flex-shrink-0">
                    <p className="text-slate-400 text-xs mb-2">Current Logo</p>
                    <div className="w-48 h-24 bg-slate-900 border border-slate-700 rounded-xl flex items-center justify-center p-4">
                      <img
                        src={platformLogoUrl}
                        alt="Platform Logo"
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => { e.target.src = '/placeholder-logo.svg'; }}
                      />
                    </div>
                  </div>

                  {/* Upload Section */}
                  <div className="flex-grow">
                    <div className="bg-slate-900/50 border border-slate-700 border-dashed rounded-xl p-6">
                      <div className="text-center">
                        <Settings className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                        <p className="text-slate-300 mb-2">Upload a new platform logo</p>
                        <p className="text-slate-500 text-xs mb-4">
                          Recommended: 400x100px, transparent PNG or SVG
                          <br />Maximum size: 2MB
                        </p>

                        <input
                          type="file"
                          id="platform-logo-upload"
                          accept="image/png,image/jpeg,image/svg+xml,image/webp"
                          onChange={handlePlatformLogoUpload}
                          className="hidden"
                          data-testid="platform-logo-input"
                        />
                        <label
                          htmlFor="platform-logo-upload"
                          className={`inline-flex items-center px-4 py-2 rounded-lg cursor-pointer transition-colors ${uploadingPlatformLogo
                            ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                            : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                            }`}
                        >
                          {uploadingPlatformLogo ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4 mr-2" />
                              Upload Logo
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info Box */}
                <div className="mt-6 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                  <p className="text-emerald-300 text-sm">
                    <strong>Note:</strong> The platform logo is used in:
                  </p>
                  <ul className="text-emerald-200/70 text-sm mt-2 space-y-1 list-disc list-inside">
                    <li>Dashboard header (alongside organisation logo)</li>
                    <li>PDF report footer ("Powered by Lucent Sky ESG")</li>
                    <li>Public-facing pages and landing page</li>
                  </ul>
                </div>
              </div>

              {/* Platform Contact Details Section */}
              <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 mt-6">
                <h4 className="text-white font-semibold mb-4 flex items-center">
                  <Building2 className="w-5 h-5 mr-2 text-emerald-400" />
                  Platform Contact Details
                </h4>
                <p className="text-slate-400 text-sm mb-6">
                  These details appear in PDF reports, website footer, and contact sections.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Business Name
                    </label>
                    <input
                      type="text"
                      value={platformSettings.business_name}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, business_name: e.target.value })}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="Lucent Sky ESG"
                      data-testid="platform-business-name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      ABN
                    </label>
                    <input
                      type="text"
                      value={platformSettings.business_abn}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, business_abn: e.target.value })}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="37 351 283 491"
                      data-testid="platform-business-abn"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={platformSettings.business_email}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, business_email: e.target.value })}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="hello@lucentsky.com.au"
                      data-testid="platform-business-email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={platformSettings.business_phone}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, business_phone: e.target.value })}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="+61 400 000 000"
                      data-testid="platform-business-phone"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Business Address
                    </label>
                    <input
                      type="text"
                      value={platformSettings.business_address}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, business_address: e.target.value })}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="64 Meadow Court, Doonan, QLD, 4562"
                      data-testid="platform-business-address"
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <Button
                    onClick={handleSavePlatformSettings}
                    disabled={savingPlatformSettings}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="save-platform-settings"
                  >
                    {savingPlatformSettings ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Contact Details
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )
        }
      </main >

      {/* View Members Modal */}
      {
        membersModalOrg && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-lg w-full overflow-hidden flex flex-col max-h-[90vh]">
              <div className="p-6 border-b border-slate-700 flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold text-white">Organisation Members</h3>
                  <p className="text-sm text-slate-400 mt-1">{membersModalOrg.name}</p>
                </div>
                <button
                  onClick={() => setMembersModalOrg(null)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto">
                {loadingMembers ? (
                  <div className="flex justify-center items-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-slate-500" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orgMembers.map((m, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
                        <div>
                          <p className="font-medium text-slate-200">{m.user?.name || 'Unknown'}</p>
                          <p className="text-xs text-slate-500 font-mono mt-0.5">{m.user?.email || 'Unknown'}</p>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className={`px-2 py-1 rounded text-xs font-semibold uppercase tracking-wider ${m.role === 'owner' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' :
                            m.role === 'admin' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                              m.role === 'editor' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                                'bg-slate-700/50 text-slate-400 border border-slate-600'
                            }`}>
                            {m.role}
                          </span>
                          {m.role !== 'owner' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!m.user?.id) {
                                  toast.error("Unknown User ID");
                                  return;
                                }
                                setConfirmRemoveMember({
                                  user_id: m.user.id,
                                  org_id: membersModalOrg.id,
                                  name: m.user?.name,
                                  email: m.user?.email
                                });
                              }}
                              className="p-1.5 h-7 w-7 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-md transition-colors"
                              title="Remove User"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    {orgMembers.length === 0 && (
                      <p className="text-slate-400 text-center py-4">No members found.</p>
                    )}
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-slate-700 bg-slate-900/50 flex justify-end">
                <Button onClick={() => setMembersModalOrg(null)} variant="outline" className="border-slate-600 text-slate-300">
                  Close
                </Button>
              </div>
            </div>

            {/* Member Removal Confirmation Overlay */}
            {confirmRemoveMember && (
              <div className="absolute inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/90 backdrop-blur-sm rounded-xl">
                <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-sm overflow-hidden flex flex-col shadow-2xl">
                  <div className="p-5 border-b border-slate-700">
                    <h3 className="text-lg font-bold text-red-400 flex items-center">
                      <AlertTriangle className="w-5 h-5 mr-2" />
                      Confirm Removal
                    </h3>
                  </div>
                  <div className="p-5">
                    <p className="text-sm text-slate-300">
                      Are you sure you want to remove <strong className="text-white">{confirmRemoveMember.name || confirmRemoveMember.email || 'this user'}</strong> from the organisation?
                    </p>
                  </div>
                  <div className="p-5 border-t border-slate-700 bg-slate-900/50 flex justify-end space-x-3">
                    <Button
                      onClick={() => setConfirmRemoveMember(null)}
                      variant="outline"
                      className="border-slate-600 text-slate-300"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => handleRemoveUserFromOrg(confirmRemoveMember.user_id, confirmRemoveMember.org_id)}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      }

      {/* Transfer Ownership Modal */}
      {
        transferModalOrg && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-lg w-full overflow-hidden flex flex-col">
              <div className="p-6 border-b border-slate-700">
                <h3 className="text-xl font-bold text-white flex items-center">
                  <Settings className="w-5 h-5 mr-2 text-blue-400" />
                  Transfer Ownership
                </h3>
                <p className="text-slate-400 mt-2 text-sm">
                  You are transferring ownership of <strong>{transferModalOrg.name}</strong>. The new owner must already have an account on the platform.
                </p>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-300 block mb-1">New Owner's Email Address:</label>
                  <Input
                    type="email"
                    placeholder="e.g. name@example.com"
                    value={transferOwnerEmail}
                    onChange={(e) => setTransferOwnerEmail(e.target.value)}
                    className="bg-slate-900 border-slate-700 text-white w-full"
                  />
                </div>
              </div>

              <div className="p-6 border-t border-slate-700 bg-slate-900/50 flex justify-end space-x-3">
                <Button
                  onClick={() => setTransferModalOrg(null)}
                  variant="outline"
                  className="border-slate-600 text-slate-300"
                  disabled={transferringOwnership}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAdminTransferOwnership}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={transferringOwnership || !transferOwnerEmail}
                >
                  {transferringOwnership ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Transfer Ownership
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )
      }

      {/* Delete Organisation Modal */}
      {
        deleteModalOrg && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-lg w-full overflow-hidden flex flex-col">
              <div className="p-6 border-b border-slate-700">
                <h3 className="text-xl font-bold text-red-500 flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  Delete Organisation
                </h3>
                <p className="text-sm text-slate-400 mt-2">
                  This action is <strong>irreversible</strong> and will permanently delete the organisation <strong className="text-white">{deleteModalOrg.name}</strong> and all associated data, including entries, metrics, and documents.
                </p>
              </div>

              <div className="p-6 space-y-4">
                <label className="block text-sm font-medium text-slate-300">
                  To confirm, type <span className="font-mono text-white bg-slate-900 px-1 py-0.5 rounded">{deleteModalOrg.name}</span> below:
                </label>
                <input
                  type="text"
                  value={deleteOrgConfirmText}
                  onChange={(e) => setDeleteOrgConfirmText(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
                  placeholder={deleteModalOrg.name}
                />
              </div>

              <div className="p-6 border-t border-slate-700 bg-slate-900/50 flex justify-end space-x-3">
                <Button
                  onClick={() => {
                    setDeleteModalOrg(null);
                    setDeleteOrgConfirmText('');
                  }}
                  variant="outline"
                  className="border-slate-600 text-slate-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    if (deleteOrgConfirmText !== deleteModalOrg.name) return;
                    setDeletingOrg(true);
                    try {
                      await callAdminFunction('delete_organisation', { organisation_id: deleteModalOrg.id });
                      toast.success("Organisation successfully deleted.");
                      setDeleteModalOrg(null);
                      setDeleteOrgConfirmText('');
                      fetchOrganisations();
                    } catch (err) {
                      console.error("Delete org error:", err);
                      toast.error("Failed to delete organisation. " + (err.message || ''));
                    } finally {
                      setDeletingOrg(false);
                    }
                  }}
                  disabled={deletingOrg || deleteOrgConfirmText !== deleteModalOrg.name}
                  className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                >
                  {deletingOrg ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Permanently Delete'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )
      }

      {/* Edit User Modal */}
      {
        editUserModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-lg w-full overflow-hidden flex flex-col">
              <div className="p-6 border-b border-slate-700">
                <h3 className="text-xl font-bold text-white flex items-center">
                  <Edit2 className="w-5 h-5 mr-2 text-blue-400" />
                  Edit User Details
                </h3>
                <p className="text-slate-400 mt-2 text-sm">
                  Modify the name associated with the account: <strong className="text-white">{editUserModal.email}</strong>
                </p>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-300 block mb-1">Account Name:</label>
                  <Input
                    type="text"
                    placeholder="e.g. John Doe"
                    value={editUserName}
                    onChange={(e) => setEditUserName(e.target.value)}
                    className="bg-slate-900 border-slate-700 text-white w-full"
                  />
                </div>
              </div>
              <div className="p-6 border-t border-slate-700 bg-slate-900/50 flex justify-end space-x-3">
                <Button
                  onClick={() => setEditUserModal(null)}
                  variant="outline"
                  className="border-slate-600 text-slate-300"
                  disabled={savingUserName}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateUserName}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={savingUserName || !editUserName.trim()}
                >
                  {savingUserName ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )
      }

      {/* Subscription Override Modal */}
      {
        overrideModalOrg && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-slate-800 rounded-2xl border border-slate-700 w-full max-w-md overflow-hidden">
              <div className="p-6 border-b border-slate-700">
                <h3 className="text-xl font-semibold text-white">Subscription Override</h3>
                <p className="text-slate-400 text-sm mt-1">{overrideModalOrg.name}</p>
              </div>

              <div className="p-6 space-y-5">
                {/* Override Type */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Override Type
                  </label>
                  <Select value={overrideType} onValueChange={setOverrideType}>
                    <SelectTrigger className="w-full bg-slate-900 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="system_default" className="text-slate-300">
                        <div className="flex items-center">
                          <Settings className="w-4 h-4 mr-2 text-slate-400" />
                          System Default (Follow Stripe)
                        </div>
                      </SelectItem>
                      <SelectItem value="manual_pro" className="text-purple-400">
                        <div className="flex items-center">
                          <Crown className="w-4 h-4 mr-2" />
                          Manual Pro (Free Access)
                        </div>
                      </SelectItem>
                      <SelectItem value="manual_standard" className="text-blue-400">
                        <div className="flex items-center">
                          <Star className="w-4 h-4 mr-2" />
                          Manual Standard (Free Access)
                        </div>
                      </SelectItem>
                      <SelectItem value="manual_demo" className="text-amber-400">
                        <div className="flex items-center">
                          <AlertTriangle className="w-4 h-4 mr-2" />
                          Manual Demo Sandbox (Restricted)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>

                  {overrideType === 'manual_pro' && (
                    <p className="mt-2 text-xs text-purple-400/80">
                      User will get unlimited entries, Grant Vault access, and un-watermarked PDF reports without payment.
                    </p>
                  )}
                  {overrideType === 'manual_standard' && (
                    <p className="mt-2 text-xs text-blue-400/80">
                      User will get Standard tier features (10 entries, no PDF export) without payment.
                    </p>
                  )}
                  {overrideType === 'manual_demo' && (
                    <p className="mt-2 text-xs text-amber-400/80">
                      User will be restricted to Demo tier (5 entries) regardless of any Stripe payments.
                    </p>
                  )}
                </div>

                {/* Reason */}
                {overrideType !== 'system_default' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Reason (Optional)
                    </label>
                    <Input
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      placeholder="e.g., Partner account, Beta tester, 3-month comp"
                      className="bg-slate-900 border-slate-700 text-white placeholder-slate-500"
                    />
                  </div>
                )}

                {/* Expiry Date */}
                {overrideType !== 'system_default' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Expiry Date (Optional)
                    </label>
                    <Input
                      type="date"
                      value={overrideExpiry}
                      onChange={(e) => setOverrideExpiry(e.target.value)}
                      className="bg-slate-900 border-slate-700 text-white"
                      min={new Date().toISOString().split('T')[0]}
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      Leave blank for indefinite access
                    </p>
                  </div>
                )}
              </div>

              <div className="p-6 bg-slate-900/50 border-t border-slate-700 flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setOverrideModalOrg(null)}
                  className="border-slate-600 text-slate-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveSubscriptionOverride}
                  disabled={savingOverride}
                  className={`${overrideType === 'manual_pro'
                    ? 'bg-purple-600 hover:bg-purple-700'
                    : overrideType === 'manual_standard'
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : overrideType === 'manual_demo'
                        ? 'bg-amber-600 hover:bg-amber-700'
                        : 'bg-[#74A57F] hover:bg-[#5F8575]'
                    } text-white`}
                >
                  {savingOverride ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Save Override
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )
      }

      {/* Create Admin Account Modal */}
      {
        showCreateAdminModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-2xl border border-slate-700 w-full max-w-md p-6 mx-4">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Create Admin Account</h3>
                </div>
                <Button
                  onClick={() => setShowCreateAdminModal(false)}
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-slate-400 text-sm mb-1 block">Full Name</label>
                  <Input
                    value={newAdminData.name}
                    onChange={(e) => setNewAdminData({ ...newAdminData, name: e.target.value })}
                    placeholder="John Smith"
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>

                <div>
                  <label className="text-slate-400 text-sm mb-1 block">Email</label>
                  <Input
                    type="email"
                    value={newAdminData.email}
                    onChange={(e) => setNewAdminData({ ...newAdminData, email: e.target.value })}
                    placeholder="admin@lucentsky.com.au"
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>

                <div>
                  <label className="text-slate-400 text-sm mb-1 block">Password</label>
                  <Input
                    type="text"
                    value={newAdminData.password}
                    onChange={(e) => setNewAdminData({ ...newAdminData, password: e.target.value })}
                    placeholder="SecurePassword123!"
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                  <p className="text-slate-500 text-xs mt-1">Password will be visible for easy copying</p>
                </div>

                <div>
                  <label className="text-slate-400 text-sm mb-1 block">Role</label>
                  <Select
                    value={newAdminData.role}
                    onValueChange={(value) => setNewAdminData({ ...newAdminData, role: value })}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="admin" className="text-purple-400 hover:bg-slate-700">
                        <div className="flex items-center">
                          <Shield className="w-4 h-4 mr-2" />
                          Admin
                        </div>
                      </SelectItem>
                      {adminStatus?.email?.toLowerCase() === 'timothy.herd@gmail.com' && (
                        <SelectItem value="superadmin" className="text-amber-400 hover:bg-slate-700">
                          <div className="flex items-center">
                            <Crown className="w-4 h-4 mr-2" />
                            SuperAdmin
                          </div>
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-slate-500 text-xs mt-1">
                    {newAdminData.role === 'superadmin'
                      ? 'SuperAdmin has full system access and can create other SuperAdmins'
                      : 'Admin can manage organisations, overrides, and create test accounts'}
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  onClick={() => setShowCreateAdminModal(false)}
                  variant="outline"
                  className="border-slate-600 text-slate-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={createAdminAccount}
                  disabled={creatingAdmin}
                  className={`${newAdminData.role === 'superadmin' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-purple-500 hover:bg-purple-600'} text-white`}
                >
                  {creatingAdmin ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Create {newAdminData.role === 'superadmin' ? 'SuperAdmin' : 'Admin'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )
      }
      {/* All Users Tab */}
      {
        activeTab === 'allusers' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center space-x-2">
                <Users className="w-6 h-6 text-blue-400" />
                <span>All Platform Users</span>
                <span className="text-sm font-normal text-slate-400 ml-2">({allUsers.length})</span>
              </h2>
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    placeholder="Search by email or name..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className="pl-9 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 w-72"
                  />
                </div>
                <Button variant="outline" size="sm" onClick={fetchAllUsers} className="border-slate-700 text-slate-300">
                  <RefreshCw className={`w-4 h-4 ${loadingUsers ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>

            {loadingUsers ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
              </div>
            ) : (
              <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">System Role</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Organisation(s)</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Tickets (Open/Total)</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Joined</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {allUsers
                      .filter(u => {
                        if (!userSearchTerm) return true;
                        const term = userSearchTerm.toLowerCase();
                        return (u.email || '').toLowerCase().includes(term) ||
                          (u.name || '').toLowerCase().includes(term) ||
                          u.orgRoles.some(r => (r.org_name || '').toLowerCase().includes(term));
                      })
                      .slice(0, 100)
                      .map(u => (
                        <tr key={u.id} className="hover:bg-slate-750">
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-white">{u.name || '—'}</div>
                            <div className="text-xs text-slate-400">{u.email}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${u.role === 'superadmin' ? 'bg-amber-500/20 text-amber-300' :
                              u.role === 'admin' ? 'bg-purple-500/20 text-purple-300' :
                                'bg-slate-700 text-slate-300'
                              }`}>
                              {u.role || 'user'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {u.orgRoles.length === 0 ? (
                              <span className="text-xs text-slate-500">No org</span>
                            ) : (
                              <div className="space-y-1">
                                {u.orgRoles.map((r, i) => (
                                  <div key={i} className="flex items-center space-x-2">
                                    <span className="text-xs text-slate-300">{r.org_name}</span>
                                    <span className={`px-1.5 py-0.5 rounded text-xs ${r.role === 'owner' ? 'bg-amber-500/20 text-amber-300' :
                                      r.role === 'full_access' ? 'bg-purple-500/20 text-purple-300' :
                                        r.role === 'editor' ? 'bg-blue-500/20 text-blue-300' :
                                          'bg-slate-700 text-slate-400'
                                      }`}>
                                      {r.role}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-300">
                            <span className={u.support_tickets_open > 0 ? "text-amber-400 font-medium" : ""}>
                              {u.support_tickets_open}
                            </span>
                            <span className="text-slate-500 mx-1">/</span>
                            {u.support_tickets_raised}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-400">
                            {new Date(u.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <Button
                                onClick={() => {
                                  setOrgManageModalUser(u);
                                  if (organisations.length === 0) fetchOrganisations();
                                }}
                                variant="ghost"
                                size="sm"
                                className="text-slate-400 hover:text-white"
                                title="Manage Organisations"
                              >
                                <Building2 className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => {
                                  setEditUserModal(u);
                                  setEditUserName(u.name || '');
                                }}
                                variant="ghost"
                                size="sm"
                                className="text-slate-400 hover:text-white"
                                title="Edit User Name"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => handleSendPasswordReset(u)}
                                disabled={sendingResetId === u.id}
                                variant="ghost"
                                size="sm"
                                className="text-slate-400 hover:text-white"
                                title="Send Password Reset Link"
                              >
                                {sendingResetId === u.id ? (
                                  <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                ) : (
                                  <Mail className="w-4 h-4 mr-1" />
                                )}
                                Reset Password
                              </Button>

                              <Button
                                onClick={() => setDeleteModalUser(u)}
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                                title="Delete User"
                                disabled={u.id === user?.id || u.role === 'superadmin'}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      }

      {/* Roles Matrix Tab */}
      {
        activeTab === 'roles' && (
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-4">
              <Table className="w-6 h-6 text-indigo-400" />
              <h2 className="text-xl font-bold text-white">Roles & Permissions Matrix</h2>
            </div>
            <p className="text-sm text-slate-400 mb-4">Read-only reference of the permissions configured in <code className="bg-slate-800 px-1.5 py-0.5 rounded text-indigo-300">permissionsMatrix.js</code>. This shows what each role can do at each subscription tier.</p>

            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-900 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-slate-400 font-medium min-w-[200px]">Feature</th>
                    {Object.values(TIERS).map(tier => (
                      Object.values(ROLES).map(role => (
                        <th key={`${tier}-${role}`} className="px-2 py-2 text-center text-slate-400 font-medium">
                          <div className="text-[10px] uppercase text-slate-500">{tier}</div>
                          <div>{role}</div>
                        </th>
                      ))
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {Object.entries(FEATURES).map(([featureKey, featureValue]) => (
                    <tr key={featureKey} className="hover:bg-slate-750">
                      <td className="px-3 py-2 text-slate-300 font-medium whitespace-nowrap">{featureValue}</td>
                      {Object.values(TIERS).map(tier => (
                        Object.values(ROLES).map(role => {
                          const access = getAccess(featureValue, tier, role);
                          let cellClass = '';
                          let cellText = '';
                          if (access === true) { cellClass = 'text-emerald-400'; cellText = '✓'; }
                          else if (access === false) { cellClass = 'text-red-400'; cellText = '✗'; }
                          else if (typeof access === 'string' && access.startsWith('upgrade:')) { cellClass = 'text-amber-400'; cellText = '⬆'; }
                          else if (access === 'hidden') { cellClass = 'text-slate-600'; cellText = '—'; }
                          else if (access === 'coming_soon') { cellClass = 'text-blue-400'; cellText = '⏳'; }
                          else { cellClass = 'text-slate-500'; cellText = String(access); }
                          return (
                            <td key={`${tier}-${role}`} className={`px-2 py-2 text-center font-medium ${cellClass}`} title={String(access)}>
                              {cellText}
                            </td>
                          );
                        })
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center space-x-6 text-xs text-slate-500 mt-2">
              <span><span className="text-emerald-400">✓</span> Allowed</span>
              <span><span className="text-red-400">✗</span> Denied</span>
              <span><span className="text-amber-400">⬆</span> Upgrade required</span>
              <span><span className="text-slate-600">—</span> Hidden</span>
              <span><span className="text-blue-400">⏳</span> Coming soon</span>
            </div>
          </div>
        )
      }

      {/* Manage User Organisations Modal */}
      {
        orgManageModalUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-lg w-full overflow-hidden flex flex-col max-h-[90vh]">
              <div className="p-6 border-b border-slate-700 flex justify-between items-center">
                <h3 className="text-lg font-bold text-white flex items-center">
                  <Building2 className="w-5 h-5 mr-2 text-blue-400" />
                  Manage Memberships
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setOrgManageModalUser(null)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="p-6 overflow-y-auto custom-scrollbar">
                <h4 className="text-sm font-medium text-slate-300 mb-3 text-center">
                  {orgManageModalUser.name} ({orgManageModalUser.email})
                </h4>

                {/* Current memberships */}
                <div className="mb-6 space-y-2">
                  <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Current Organisations</h5>
                  {orgManageModalUser.orgRoles.length === 0 ? (
                    <div className="text-sm text-slate-500">Not a member of any organisation.</div>
                  ) : (
                    orgManageModalUser.orgRoles.map((r, i) => (
                      <div key={i} className="flex items-center justify-between bg-slate-750 p-3 rounded-lg border border-slate-700">
                        <div>
                          <div className="text-sm text-white font-medium">{r.org_name}</div>
                          <div className="text-xs text-slate-400 mt-0.5 inline-block mr-2 capitalize">{r.role.replace('_', ' ')}</div>
                        </div>
                        {r.role === 'owner' ? (
                          <span className="text-xs text-amber-500 bg-amber-500/10 px-2 py-1 rounded">Owner (Cannot remove)</span>
                        ) : (
                          <Button
                            onClick={() => handleRemoveUserFromOrg(orgManageModalUser.id, r.org_id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-400/10 h-8 text-xs font-medium"
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    ))
                  )}
                </div>

                {/* Add to organisation */}
                <div className="pt-4 border-t border-slate-700">
                  <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Add to Organisation</h5>
                  <div className="flex space-x-2">
                    <Select value={selectedOrgToAdd} onValueChange={setSelectedOrgToAdd}>
                      <SelectTrigger className="bg-slate-900 border-slate-700 text-white w-full">
                        <SelectValue placeholder="Select organisation..." />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                        {organisations.filter(o => !orgManageModalUser.orgRoles.find(r => r.org_id === o.id)).map(org => (
                          <SelectItem key={org.id} value={org.id} className="text-slate-200 focus:bg-slate-700 focus:text-white">
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end space-x-2 mt-3">
                    <div className="flex-1">
                      <Select value={selectedRoleToAdd} onValueChange={setSelectedRoleToAdd}>
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="viewer" className="text-slate-200">Viewer</SelectItem>
                          <SelectItem value="editor" className="text-slate-200">Editor</SelectItem>
                          <SelectItem value="full_access" className="text-slate-200">Full Access</SelectItem>
                          {/* Cannot assign owner role here, only transfer ownership logic does that */}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={() => handleAddUserToOrg(orgManageModalUser.id, selectedOrgToAdd, selectedRoleToAdd)}
                      disabled={managingOrgUser || !selectedOrgToAdd}
                      className="bg-blue-600 hover:bg-blue-700 text-white min-w-[100px]"
                    >
                      {managingOrgUser ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add User'}
                    </Button>
                  </div>
                </div>

              </div>

            </div>
          </div>
        )
      }
    </div >
  );
};

export default AdminDashboardPage;
